/**
 * /experiencia-3d — El cerebro de Aynara en vivo
 * Rediseño v2.0 — Sep 2026
 * ⚠ NO tocar la lógica de llamada/audio/WebRTC, solo la capa visual.
 * Three.js se carga en lazy (dynamic import), nunca afecta el resto del sitio.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Check, ChevronDown, Link2, MessageCircle, Mic, Moon,
  PhoneCall, Radio, Share2, Sparkles, Sun, Volume2, VolumeX, X,
} from 'lucide-react';
import SEO from '../components/SEO';

// ─── Paleta de marca ─────────────────────────────────────────────────────────
const NAVY_BG = '#0a1a3f';
const CYAN    = '#22d3ee';
const GOLD    = '#f2b441';
const DIM     = '#071020';

// ─── Nombres de plantas del edificio ─────────────────────────────────────────
const FLOOR_NAMES = [
  'Ventas & Cierre',       // piso 1
  'Marketing Digital',      // piso 2
  'Soporte al Cliente',    // piso 3
  'Datos & Analítica',     // piso 4
  'Gestión de Proyectos',  // piso 5
  'Recursos Humanos',      // piso 6
  'Finanzas',              // piso 7
  'Operaciones',           // piso 8
  'I+D & Innovación',      // piso 9
  'Dirección Ejecutiva',   // piso 10
];

// ─── Herramientas del ecosistema ─────────────────────────────────────────────
const TOOLS = [
  { id: 'gmail',          name: 'Gmail',        logo: 'https://logos.composio.dev/api/gmail' },
  { id: 'hubspot',        name: 'HubSpot',       logo: 'https://logos.composio.dev/api/hubspot' },
  { id: 'notion',         name: 'Notion',        logo: 'https://logos.composio.dev/api/notion' },
  { id: 'googledrive',    name: 'Drive',         logo: 'https://logos.composio.dev/api/googledrive' },
  { id: 'slack',          name: 'Slack',         logo: 'https://logos.composio.dev/api/slack' },
  { id: 'googlecalendar', name: 'Calendar',      logo: 'https://logos.composio.dev/api/googlecalendar' },
  { id: 'outlook',        name: 'Outlook',       logo: 'https://logos.composio.dev/api/outlook' },
  { id: 'googlesheets',   name: 'Sheets',        logo: 'https://logos.composio.dev/api/googlesheets' },
];

// Config de animación por estado
const ORB_CFG = {
  idle:       { speed: 0.18, glow: 0.28, scale: 1.00, ring: 0.0, particleSize: 0.038 },
  listening:  { speed: 0.52, glow: 0.72, scale: 1.04, ring: 0.35, particleSize: 0.046 },
  thinking:   { speed: 1.30, glow: 1.15, scale: 1.10, ring: 0.55, particleSize: 0.036 },
  responding: { speed: 0.65, glow: 0.95, scale: 1.07, ring: 0.80, particleSize: 0.050 },
};

// Transcripción de demostración (sustituida por MutationObserver real si disponible)
const DEMO_LINES = [
  { who: 'USER',   text: 'Buenos días, necesito automatizar seguimiento de clientes.' },
  { who: 'AYNARA', text: 'Activando CRM + Gmail. Conectando HubSpot…' },
  { who: 'AYNARA', text: 'Creando secuencia de seguimiento en 3 pasos.' },
  { who: 'USER',   text: '¿Puedes añadir recordatorio por WhatsApp?' },
  { who: 'AYNARA', text: 'WhatsApp Business añadido al flujo.' },
  { who: 'AYNARA', text: 'Listo: secuencia activa para tus próximos 47 leads.' },
  { who: 'USER',   text: 'Genera también un informe semanal en Notion.' },
  { who: 'AYNARA', text: 'Conectando Notion Docs+. Plantilla de informe creada.' },
];

const MODULES = [
  { id: 'nv-engine', name: 'NV-Engine', desc: 'Motor de generación y ejecución de tareas de alto volumen.' },
  { id: 'docs', name: 'Neuralia Docs+', desc: 'Documentos vivos: informes, propuestas y contratos generados por agentes.' },
  { id: 'syntx', name: 'Syntx 369', desc: 'Capa de automatización y flujos inteligentes entre herramientas.' },
  { id: 'memoria', name: 'Memoria Operativa 369', desc: 'Memoria persistente del negocio: cada interacción queda registrada.' },
  { id: 'lonmaikon', name: 'Lonmaikon', desc: 'Módulo de operaciones industriales y control de dispositivos.' },
  { id: 'voicebox', name: 'Neuralia Voicebox', desc: 'Canal de voz: comandos hablados y agente telefónico (Bland.ai).' },
  { id: 'core', name: 'Neuralia Core', desc: 'El corazón del sistema: identidad, permisos y coordinación central.' },
  { id: 'router', name: 'Neuralia Router', desc: 'Decide qué agente responde cada intención, en cualquier canal.' },
  { id: 'mcp', name: 'Neuralia MCP Layer', desc: 'Conexión universal con herramientas externas vía protocolo MCP.' },
];

const AGENTS = [
  { name: 'Aynara', spec: 'Orquestadora central', status: 'activo' },
  { name: 'Vendedor 369', spec: 'Ventas y cierre de leads', status: 'activo' },
  { name: 'Copywriter', spec: 'Textos y campañas', status: 'activo' },
  { name: 'Diseñador', spec: 'Assets visuales y branding', status: 'en misión' },
  { name: 'Analista', spec: 'Datos, KPIs y forecasting', status: 'activo' },
  { name: 'Facturador', spec: 'Cobros y facturación', status: 'activo' },
  { name: 'Soporte', spec: 'Atención al cliente 24/7', status: 'activo' },
  { name: 'Agendador', spec: 'Reservas y calendario', status: 'activo' },
  { name: 'Investigador', spec: 'Mercado y competencia', status: 'en misión' },
  { name: 'Media Buyer', spec: 'Meta y Google Ads', status: 'entrenando' },
  { name: 'Legal Docs', spec: 'Contratos y firmas', status: 'activo' },
  { name: 'Operador IoT', spec: 'Máquinas y robots', status: 'entrenando' },
  { name: 'Reclutador', spec: 'Talento y onboarding', status: 'en misión' },
];
const AGENT_STATUS_STYLE = {
  activo: 'bg-emerald-400',
  'en misión': 'bg-[#22d3ee]',
  entrenando: 'bg-amber-400',
};

const lerp = (a, b, t) => a + (b - a) * t;

// ─── Pool de 155 agentes (nivel de módulo para tour + Three.js) ───────────────
const _AG_SPECS = [
  'Automatización de procesos','Análisis de datos avanzado','Generación de reportes',
  'Gestión inteligente de correo','Sincronización CRM','Seguimiento de leads',
  'Publicación en redes sociales','Integración de webhooks','Monitoreo de métricas',
  'Gestión de calendario','Respuesta automática 24/7','Procesamiento de pagos',
  'Gestión de inventario','Análisis de sentimientos','Optimización SEO',
  'Generación de contenido IA','Atención multicanal','Integración de APIs',
  'Gestión de contratos','Análisis competitivo','Orquestación de tareas',
  'Clasificación de leads','Enriquecimiento de datos','Pipeline de ventas',
];
const _AG_SEQ = ['activo','activo','activo','activo','activo','activo','en misión','en misión','en misión','entrenando','entrenando'];
const AGENT_POOL_DATA = (() => {
  const p = [
    { name:'Aynara',       spec:'Orquestadora central',       status:'activo'     },
    { name:'Vendedor 369', spec:'Ventas y cierre de leads',    status:'activo'     },
    { name:'Copywriter',   spec:'Textos y campañas',           status:'activo'     },
    { name:'Diseñador',    spec:'Assets visuales y branding',  status:'en misión'  },
    { name:'Analista',     spec:'Datos, KPIs y forecasting',   status:'activo'     },
    { name:'Facturador',   spec:'Cobros y facturación',        status:'activo'     },
    { name:'Soporte',      spec:'Atención al cliente 24/7',    status:'activo'     },
    { name:'Agendador',    spec:'Reservas y calendario',       status:'activo'     },
    { name:'Investigador', spec:'Mercado y competencia',       status:'en misión'  },
    { name:'Media Buyer',  spec:'Meta y Google Ads',           status:'entrenando' },
    { name:'Legal Docs',   spec:'Contratos y firmas',          status:'activo'     },
    { name:'Operador IoT', spec:'Máquinas y robots',           status:'entrenando' },
    { name:'Reclutador',   spec:'Talento y onboarding',        status:'en misión'  },
  ];
  let n = 1;
  while (p.length < 155) {
    p.push({ name:`Agente-${String(n++).padStart(3,'0')}`, spec:_AG_SPECS[p.length%_AG_SPECS.length], status:_AG_SEQ[p.length%_AG_SEQ.length] });
  }
  return p;
})();

// Distribución por planta del edificio
const TOUR_FLOOR_STARTS = [0,16,32,48,64,80,95,110,125,140];
const TOUR_FLOOR_COUNTS = [16,16,16,16,16,15,15,15,15,15];
const TOUR_FLOOR_NAMES  = [
  'Ventas & Cierre','Operaciones Core','Marketing Digital','Análisis & Data',
  'Atención al Cliente','Automatización','Legal & Compliance',
  'Inteligencia de Mercado','Recursos & Talento','Dirección Ejecutiva',
];

// Waypoints del tour guiado (12 pasos: overview + 10 plantas + Aynara crown)
const _BY = -(10 * 0.88) / 2; // BLDG_BOT = -4.4
const _AY   = _BY + 10 * 0.88 + 1.4; // AYNARA_Y = 5.8
const _N369Y = _AY + 0.6;             // Logo Billboard Y = 6.4
const TOUR_WAYPOINTS = [
  {
    id:'logo-zoom', title:'NEURALIA369',
    subtitle:'El sistema de agentes IA más avanzado de Latinoamérica',
    cam:{x:1.5, y:3.5, z:12}, lookAt:{x:0, y:_N369Y, z:0},
    floor:-3, duration:2200,
  },
  {
    id:'overview', title:'NEURALIA369',
    subtitle:'155 especialistas IA · 19 equipos · Activos 24/7',
    cam:{x:5.5,y:1.2,z:12}, lookAt:{x:0,y:0.8,z:0},
    floor:-1, duration:3200,
  },
  ...Array.from({ length:10 }, (_,f) => {
    const fy = _BY + f * 0.88 + 0.88 * 0.55;
    const agents = AGENT_POOL_DATA.slice(TOUR_FLOOR_STARTS[f], TOUR_FLOOR_STARTS[f]+TOUR_FLOOR_COUNTS[f]);
    const activo     = agents.filter(a=>a.status==='activo').length;
    const enMision   = agents.filter(a=>a.status==='en misión').length;
    const entrenando = agents.filter(a=>a.status==='entrenando').length;
    return {
      id:`floor-${f}`, title:`PLANTA ${f+1}`,
      floorName:TOUR_FLOOR_NAMES[f],
      subtitle:`${activo} activos · ${enMision} en misión · ${entrenando} entrenando`,
      cam:{x:4.5,y:fy,z:6}, lookAt:{x:0,y:fy,z:0},
      floor:f, agents:agents.slice(0,6),
      activo, enMision, entrenando, duration:2500,
    };
  }),
  {
    id:'aynara', title:'AYNARA',
    subtitle:'Orquestadora central · La inteligencia que piensa contigo',
    cam:{x:2.5,y:_AY,z:5}, lookAt:{x:0,y:_AY,z:0},
    floor:-2, duration:3500,
  },
];

// ─── Transcription HUD ───────────────────────────────────────────────────────
function TranscriptionHUD({ lines, isActive }) {
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [lines]);
  if (!isActive && lines.length === 0) return null;
  return (
    <div
      data-testid="aynara-hud-transcript"
      style={{
        position: 'absolute', bottom: 120, left: 24, width: 300,
        maxHeight: 180, overflowY: 'hidden',
        pointerEvents: 'none', zIndex: 20,
      }}
    >
      <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.2em', color: CYAN, marginBottom: 6, textTransform: 'uppercase' }}>
        NEURALIA369 · TRANSCRIPCIÓN EN VIVO {isActive && <span style={{ animation: 'blink 1s step-end infinite' }}>▌</span>}
      </p>
      {lines.map((ln, i) => (
        <div
          key={i}
          style={{
            fontFamily: 'monospace', fontSize: 11,
            color: ln.who === 'AYNARA' ? CYAN : 'rgba(255,255,255,0.82)',
            marginBottom: 4, lineHeight: 1.45,
            animation: 'hudFadeIn 0.35s ease-out both',
          }}
        >
          <span style={{ color: ln.who === 'AYNARA' ? GOLD : 'rgba(255,255,255,0.35)', marginRight: 6, fontSize: 10 }}>
            {ln.who === 'AYNARA' ? 'AYNARA ▶' : 'USUARIO ▶'}
          </span>
          {ln.text}
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}

// ─── Estado del orbe (indicador UI) ──────────────────────────────────────────
const STATE_LABELS = {
  idle:       { label: 'En espera',   color: 'rgba(34,211,238,0.4)' },
  listening:  { label: 'Escuchando',  color: CYAN },
  thinking:   { label: 'Procesando',  color: GOLD },
  responding: { label: 'Respondiendo',color: '#a78bfa' },
};

function OrbStateChip({ orbState }) {
  const cfg = STATE_LABELS[orbState];
  if (orbState === 'idle') return null;
  return (
    <div
      data-testid="aynara-orb-state"
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 10, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.2em',
        color: cfg.color, padding: '4px 10px',
        border: `1px solid ${cfg.color}40`,
        borderRadius: 99, backdropFilter: 'blur(8px)',
        background: `${cfg.color}12`,
        animation: 'hudFadeIn 0.3s ease-out',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, animation: 'pulse 1s infinite', flexShrink: 0 }} />
      {cfg.label}
    </div>
  );
}

// ─── Fallback 2D (sin WebGL o prefers-reduced-motion) ────────────────────────
function Orb2DFallback({ orbState }) {
  const spd = orbState === 'thinking' ? '0.8s' : orbState === 'idle' ? '3s' : '1.5s';
  return (
    <div
      data-testid="experiencia-3d-fallback"
      style={{
        position: 'fixed', inset: 0,
        background: `radial-gradient(ellipse 60% 55% at 50% 40%, #0a2a4a 0%, #020810 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', zIndex: 0,
      }}
    >
      {[380, 290, 200].map((s, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: s, height: s, borderRadius: '50%',
          border: `1px solid rgba(34,211,238,${0.08 + i * 0.07})`,
          animation: `orbitRing ${8 + i * 4}s linear infinite`,
          animationDirection: i % 2 === 0 ? 'normal' : 'reverse',
        }} />
      ))}
      <div style={{
        width: 160, height: 160, borderRadius: '50%', position: 'relative',
        background: `radial-gradient(circle at 38% 38%, #1a4f80, #0a1a3f)`,
        boxShadow: `0 0 60px 20px rgba(34,211,238,0.25), 0 0 120px 40px rgba(34,211,238,0.08), inset 0 0 50px rgba(34,211,238,0.12)`,
        animation: `orbPulse2d ${spd} ease-in-out infinite`,
        zIndex: 1,
      }} />
    </div>
  );
}

// ─── Panel de Tour Guiado ─────────────────────────────────────────────────────
const _SC = { activo:'#22c55e', 'en misión':'#22d3ee', entrenando:'#f59e0b' };

function TourPanel({ step, onEnd, vipName }) {
  const wp = TOUR_WAYPOINTS[step];
  if (!wp) return null;
  const total = TOUR_WAYPOINTS.length;
  const isVip = !!vipName;
  const borderColor = isVip ? 'rgba(242,180,65,0.45)' : 'rgba(34,211,238,0.26)';
  const accentColor = isVip ? GOLD : CYAN;
  return (
    <div
      data-testid="experiencia-3d-tour-panel"
      style={{
        position:'fixed', left:20, top:'50%', transform:'translateY(-50%)',
        zIndex:45, width:252, pointerEvents:'auto',
        background:'rgba(3,7,18,0.94)', backdropFilter:'blur(24px)',
        border:`1px solid ${borderColor}`,
        borderRadius:22, padding:'20px 20px 16px',
        boxShadow:`0 0 60px ${isVip ? 'rgba(242,180,65,0.18)' : 'rgba(34,211,238,0.13)'}, 0 4px 40px rgba(0,0,0,0.8)`,
        animation:'hudFadeIn 0.4s ease-out',
      }}
    >
      {/* Encabezado */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:isVip?4:12 }}>
        <span style={{ fontFamily:'monospace', fontSize:8, letterSpacing:'0.3em', color:`${accentColor}80`, textTransform:'uppercase' }}>
          {isVip ? 'Tour VIP' : 'Tour Guiado'}
        </span>
        <span style={{ fontFamily:'monospace', fontSize:9, color:'rgba(255,255,255,0.28)' }}>{step+1}<span style={{ color:'rgba(255,255,255,0.16)' }}>/{total}</span></span>
      </div>

      {/* Saludo VIP personalizado */}
      {isVip && (
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:10, padding:'4px 8px', borderRadius:99, background:`${GOLD}12`, border:`1px solid ${GOLD}35` }}>
          <span style={{ width:5, height:5, borderRadius:'50%', background:GOLD, animation:'pulse 1.8s infinite', flexShrink:0 }} />
          <span style={{ fontFamily:'monospace', fontSize:8, color:GOLD, letterSpacing:'0.18em', textTransform:'uppercase' }}>
            {vipName}
          </span>
        </div>
      )}

      {/* Título */}
      <p style={{ fontFamily:'monospace', fontWeight:900, fontSize:12, letterSpacing:'0.22em', color:'#fff', margin:'0 0 2px', textTransform:'uppercase' }}>{wp.title}</p>
      {wp.floorName && (
        <p style={{ fontFamily:'monospace', fontSize:9, color:accentColor, letterSpacing:'0.12em', margin:'0 0 6px' }}>{wp.floorName}</p>
      )}
      <p style={{ fontSize:10, color:'rgba(255,255,255,0.42)', marginBottom:10, lineHeight:1.6 }}>
        {isVip && wp.id === 'overview'
          ? `Experiencia exclusiva preparada para ti, ${vipName}`
          : wp.subtitle}
      </p>

      {/* Badges de estado */}
      {wp.floor >= 0 && (
        <div style={{ display:'flex', gap:5, marginBottom:11, flexWrap:'wrap' }}>
          {[{n:wp.activo,c:'#22c55e',l:'activos'},{n:wp.enMision,c:CYAN,l:'en misión'},{n:wp.entrenando,c:'#f59e0b',l:'entrenando'}]
            .filter(s=>s.n>0).map(s=>(
            <span key={s.l} style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:8, fontFamily:'monospace', color:s.c, background:`${s.c}14`, border:`1px solid ${s.c}35`, borderRadius:99, padding:'2px 7px', letterSpacing:'0.08em' }}>
              <span style={{ width:4, height:4, borderRadius:'50%', background:s.c, flexShrink:0 }} />
              {s.n} {s.l}
            </span>
          ))}
        </div>
      )}

      {/* Lista de agentes */}
      {wp.agents && wp.agents.length > 0 && (
        <div style={{ marginBottom:12 }}>
          {wp.agents.map((a,i)=>(
            <div key={i} style={{ display:'flex', alignItems:'center', gap:7, padding:'4px 0', borderBottom:i<wp.agents.length-1?'1px solid rgba(255,255,255,0.055)':'none' }}>
              <span style={{ width:5, height:5, borderRadius:'50%', background:_SC[a.status]||'#22c55e', flexShrink:0 }} />
              <div style={{ minWidth:0, flex:1 }}>
                <p style={{ fontSize:10, fontWeight:700, color:'#e2e8f0', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name}</p>
                <p style={{ fontSize:8, color:'rgba(255,255,255,0.36)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.spec}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barra de progreso */}
      <div style={{ height:2, background:`${accentColor}18`, borderRadius:2, marginBottom:12, overflow:'hidden' }}>
        <div key={`pb-${step}`} style={{ height:'100%', background:`linear-gradient(90deg,${accentColor},${isVip?'#ef9e20':'#3b82f6'})`, borderRadius:2, animation:`tourProgress ${wp.duration||2500}ms linear forwards` }} />
      </div>

      {/* Botón salir */}
      <button type="button" onClick={onEnd} data-testid="experiencia-3d-tour-end"
        style={{ width:'100%', background:'rgba(255,255,255,0.04)', border:`1px solid ${accentColor}20`, borderRadius:10, color:'rgba(255,255,255,0.4)', fontSize:9, fontFamily:'monospace', letterSpacing:'0.18em', textTransform:'uppercase', padding:'7px', cursor:'pointer' }}>
        {step===total-1 ? '✓ Finalizar tour' : '✕ Salir del tour'}
      </button>
    </div>
  );
}

// ─── Panel de Planta Interactivo ─────────────────────────────────────────────
const _FLOOR_SC = { activo: '#22c55e', 'en misión': '#22d3ee', entrenando: '#f2b441' };

// Color por función/especialización — accesible en Three.js y en FloorPanel
const _getAgentColor = (agent) => {
  if (!agent) return 0x22c55e;
  const s = (agent.spec || '').toLowerCase();
  if (s.includes('ventas') || s.includes('lead') || s.includes('cierre'))                  return 0xff6622;
  if (s.includes('marketing') || s.includes('contenido') || s.includes('seo') || s.includes('publicac')) return 0xcc44ff;
  if (s.includes('análisis') || s.includes('datos') || s.includes('kpi') || s.includes('reporte') || s.includes('forecast')) return 0x44aaff;
  if (s.includes('atención') || s.includes('soporte') || s.includes('cliente'))            return 0x22ee88;
  if (s.includes('automatiz') || s.includes('webhook') || s.includes('api') || s.includes('integr') || s.includes('flujo')) return 0x3b82f6;
  if (s.includes('legal') || s.includes('contrato') || s.includes('firma'))                return 0xf2b441;
  if (s.includes('diseño') || s.includes('visual') || s.includes('branding'))              return 0xff88cc;
  if (s.includes('ads') || s.includes('google') || s.includes('meta') || s.includes('media')) return 0xff4455;
  if (s.includes('pago') || s.includes('cobro') || s.includes('factur'))                   return 0x22cc44;
  if (s.includes('calendar') || s.includes('agenda') || s.includes('reserva'))             return 0xffaa22;
  if (s.includes('recruit') || s.includes('talento') || s.includes('onboard'))             return 0x88ff88;
  if (s.includes('invest') || s.includes('intel') || s.includes('mercado'))                return 0xaaaaff;
  const st = { activo: 0x22c55e, 'en misión': 0x22d3ee, entrenando: 0xf59e0b };
  return st[agent.status] || 0x22c55e;
};
const _getAgentColorHex = (agent) => `#${_getAgentColor(agent).toString(16).padStart(6, '0')}`;
const _FLOOR_TASKS = {
  activo:      ['Procesando leads', 'Generando reportes', 'Sincronizando CRM', 'Contactando clientes', 'Analizando datos', 'Ejecutando flujo'],
  'en misión': ['Analizando mercado', 'Visitando cliente', 'Reunión estratégica', 'Recopilando intel', 'Negociando contrato', 'Coordinando equipo'],
  entrenando:  ['Aprendiendo patrones', 'Calibrando modelo', 'Optimizando parámetros', 'Revisando casos', 'Mejorando precisión', 'Asimilando datos'],
};

function FloorPanel({ floorIdx, onClose, selectedAgent }) {
  const start  = TOUR_FLOOR_STARTS[floorIdx];
  const count  = TOUR_FLOOR_COUNTS[floorIdx];
  const agents = AGENT_POOL_DATA.slice(start, start + count);
  const activo     = agents.filter(a => a.status === 'activo').length;
  const enMision   = agents.filter(a => a.status === 'en misión').length;
  const entrenando = agents.filter(a => a.status === 'entrenando').length;

  return (
    <div
      data-testid="experiencia-3d-floor-panel"
      style={{
        position: 'fixed', right: 20, top: '50%', transform: 'translateY(-50%)',
        zIndex: 50, width: 288,
        maxHeight: 'calc(100vh - 140px)', overflowY: 'auto',
        background: 'rgba(3,7,18,0.97)', backdropFilter: 'blur(28px)',
        border: '1px solid rgba(34,211,238,0.28)',
        borderRadius: 22, padding: '18px 16px 14px',
        boxShadow: '0 0 60px rgba(34,211,238,0.16), 0 4px 40px rgba(0,0,0,0.85)',
        animation: 'hudFadeIn 0.35s ease-out',
        scrollbarWidth: 'thin', scrollbarColor: 'rgba(34,211,238,0.2) transparent',
      }}
    >
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: 12 }}>
        <div>
          <p style={{ fontFamily:'monospace', fontSize:8, letterSpacing:'0.35em', color:'rgba(34,211,238,0.5)', textTransform:'uppercase', margin:0 }}>
            NEURALIA369 · Planta {floorIdx + 1}
          </p>
          <p style={{ fontFamily:'monospace', fontWeight:900, fontSize:13, color:'#fff', letterSpacing:'0.2em', margin:'3px 0 0', textTransform:'uppercase' }}>
            {TOUR_FLOOR_NAMES[floorIdx]}
          </p>
        </div>
        <button type="button" onClick={onClose}
          style={{ background:'none', border:'none', color:'rgba(255,255,255,0.3)', cursor:'pointer', fontSize:15, padding:'0 2px', lineHeight:1, flexShrink:0 }}
          data-testid="experiencia-3d-floor-panel-close"
          aria-label="Cerrar"
        >✕</button>
      </div>

      {/* Metric cards */}
      <div style={{ display:'flex', gap:5, marginBottom:12 }}>
        {[{ v:activo, c:'#22c55e', l:'Trabajando' }, { v:enMision, c:CYAN, l:'En misión' }, { v:entrenando, c:GOLD, l:'Entrenando' }].map(m => (
          <div key={m.l} style={{ flex:1, background:`${m.c}0d`, border:`1px solid ${m.c}28`, borderRadius:10, padding:'7px 6px', textAlign:'center' }}>
            <p style={{ fontSize:18, fontWeight:900, color:m.c, margin:0, fontFamily:'monospace' }}>{m.v}</p>
            <p style={{ fontSize:7, color:'rgba(255,255,255,0.3)', margin:0, letterSpacing:'0.06em' }}>{m.l}</p>
          </div>
        ))}
      </div>

      {/* Agente seleccionado (al hacer clic en ventana 3D) */}
      {selectedAgent && agents.some(a => a.name === selectedAgent.name) && (() => {
        const fnCol = _getAgentColorHex(selectedAgent);
        return (
          <div style={{ marginBottom:12, padding:'9px 11px', borderRadius:13, background:`${fnCol}14`, border:`1px solid ${fnCol}45` }} data-testid="experiencia-3d-selected-agent">
            <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:5 }}>
              <span style={{ width:11, height:11, borderRadius:'50%', background:fnCol, flexShrink:0, boxShadow:`0 0 8px ${fnCol}` }} />
              <p style={{ fontSize:12, fontWeight:800, color:'#fff', margin:0, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{selectedAgent.name}</p>
              <span style={{ background:`${_FLOOR_SC[selectedAgent.status]}22`, border:`1px solid ${_FLOOR_SC[selectedAgent.status]}55`, borderRadius:99, padding:'2px 7px', fontSize:7, color:_FLOOR_SC[selectedAgent.status]||'#22c55e', fontFamily:'monospace', flexShrink:0 }}>
                {(selectedAgent.status||'').toUpperCase()}
              </span>
            </div>
            <p style={{ fontSize:9, color:`${fnCol}cc`, margin:'0 0 4px', fontFamily:'monospace', letterSpacing:'0.05em', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {selectedAgent.spec}
            </p>
            <div style={{ display:'flex', alignItems:'center', gap:5 }}>
              <span style={{ width:5, height:5, borderRadius:2, background:fnCol, flexShrink:0 }} />
              <p style={{ fontSize:8, color:'rgba(255,255,255,0.4)', margin:0, fontFamily:'monospace', letterSpacing:'0.06em' }}>
                FUNCIÓN · {selectedAgent.team || TOUR_FLOOR_NAMES[floorIdx]}
              </p>
            </div>
          </div>
        );
      })()}

      {/* Divider */}
      <div style={{ height:1, background:'rgba(34,211,238,0.1)', marginBottom:10 }} />

      {/* Agent list */}
      <p style={{ fontFamily:'monospace', fontSize:8, letterSpacing:'0.28em', color:'rgba(34,211,238,0.45)', textTransform:'uppercase', marginBottom:7 }}>
        Agentes en esta planta
      </p>
      <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
        {agents.slice(0, 14).map((a, i) => {
          const isSelected = selectedAgent && selectedAgent.name === a.name;
          const fnCol = _getAgentColorHex(a);
          return (
            <div key={i} style={{
              display:'flex', alignItems:'center', gap:7, padding:'5px 7px', borderRadius:9,
              background: isSelected ? `${fnCol}1a` : 'rgba(255,255,255,0.025)',
              border: isSelected ? `1px solid ${fnCol}55` : '1px solid rgba(255,255,255,0.04)',
              transition: 'background 0.2s',
            }}>
              {/* Punto de función (color de especialización) */}
              <span style={{ width:5, height:5, borderRadius:2, background:fnCol, flexShrink:0 }} />
              {/* Punto de estado (activo/misión/entrenando) */}
              <span style={{ width:5, height:5, borderRadius:'50%', background:_FLOOR_SC[a.status]||'#22c55e', flexShrink:0, boxShadow:`0 0 4px ${_FLOOR_SC[a.status]||'#22c55e'}99`, animation:'pulse 2s infinite' }} />
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontSize:10, fontWeight: isSelected ? 800 : 700, color: isSelected ? '#fff' : '#e2e8f0', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name}</p>
                <p style={{ fontSize:8, color: isSelected ? `${fnCol}aa` : 'rgba(255,255,255,0.32)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.spec}</p>
              </div>
              <span style={{ fontSize:7, fontFamily:'monospace', color:_FLOOR_SC[a.status]||'#22c55e', flexShrink:0, maxWidth:68, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', opacity:0.75 }}>
                {(_FLOOR_TASKS[a.status]||[])[i % 6]}
              </span>
            </div>
          );
        })}
        {agents.length > 14 && (
          <p style={{ textAlign:'center', fontSize:8, color:'rgba(34,211,238,0.35)', marginTop:3, fontFamily:'monospace', letterSpacing:'0.15em' }}>
            +{agents.length - 14} más en esta planta
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function Experiencia3DPage() {
  const mountRef      = useRef(null);
  const toolElsRef    = useRef([]);
  const lineSvgRef    = useRef(null);
  const analyserRef   = useRef(null);
  const orbStateRef   = useRef('idle');
  const activeToolRef = useRef(-1);
  const greetAudioRef = useRef(null);
  const stateTimerRef = useRef(null);
  const transcTimerRef= useRef(null);
  const demoIdxRef    = useRef(0);
  const ambientCtxRef = useRef(null);
  const ambientGainRef= useRef(null);

  const [webglOk, setWebglOk]               = useState(true);
  const [reducedMotion]                     = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [orbState, setOrbState]             = useState('idle');
  const [activeToolIdx, setActiveToolIdx]   = useState(-1);
  const [sessionActive, setSessionActive]   = useState(false);
  const [transcriptLines, setTranscriptLines] = useState([]);
  const [toolHint, setToolHint]             = useState(null);

  // Legacy UI state (preservado)
  const [callForm, setCallForm]             = useState(null);
  const [callStatus, setCallStatus]         = useState(null);
  const [activation, setActivation]         = useState({ nombre: '', email: '', empresa: '', mensaje: '' });
  const [activationSent, setActivationSent] = useState(false);
  const [busy, setBusy]                     = useState(false);
  const [greetPlaying, setGreetPlaying]     = useState(false);
  const [greetPrompt, setGreetPrompt]       = useState(false);
  const [sharePanel, setSharePanel]         = useState(false);
  const [copied, setCopied]                 = useState(false);
  const [hoverInfo, setHoverInfo]           = useState(null);
  const [winTooltip, setWinTooltip]         = useState(null); // tooltip 3D hover ventana
  const [selectedAgent, setSelectedAgent]   = useState(null);
  const [tourActive, setTourActive]         = useState(false);
  const [tourStep, setTourStep]             = useState(0);
  const tourActiveRef = useRef(false);
  const tourStepRef   = useRef(0);

  // ── Nuevas features: Panel de Planta, Modo Día/Noche, Visitante VIP ──────────
  const [selectedFloor, setSelectedFloor]   = useState(null);
  const [isDayMode, setIsDayMode]           = useState(false);
  const isDayModeRef  = useRef(false);
  const dayLerpRef    = useRef(0);
  const vipName       = useMemo(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('ref');
      return p ? decodeURIComponent(p) : null;
    } catch { return null; }
  }, []);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Sincronizar orbStateRef con state de React
  useEffect(() => { orbStateRef.current = orbState; }, [orbState]);
  useEffect(() => { activeToolRef.current = activeToolIdx; }, [activeToolIdx]);

  // ── Sincronizar refs del tour con el estado React ──────────────────────────
  useEffect(() => { tourActiveRef.current = tourActive; }, [tourActive]);
  useEffect(() => { tourStepRef.current   = tourStep;   }, [tourStep]);
  useEffect(() => { isDayModeRef.current  = isDayMode;  }, [isDayMode]);

  // ── Auto-start del tour (una vez por sesión; VIP siempre) ────────────────
  useEffect(() => {
    if (vipName) {
      // VIP: siempre mostrar el tour, ignorar "ya visto"
      sessionStorage.removeItem('neuralia-tour-seen');
      const t = setTimeout(() => setTourActive(true), 1500);
      return () => clearTimeout(t);
    }
    if (sessionStorage.getItem('neuralia-tour-seen')) return;
    const t = setTimeout(() => setTourActive(true), 2000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-avance entre pasos del tour ──────────────────────────────────────
  useEffect(() => {
    if (!tourActive) return;
    const wp = TOUR_WAYPOINTS[tourStep];
    const t = setTimeout(() => {
      if (tourStep < TOUR_WAYPOINTS.length - 1) {
        setTourStep(s => s + 1);
      } else {
        setTourActive(false);
        setTourStep(0);
        sessionStorage.setItem('neuralia-tour-seen', '1');
      }
    }, wp?.duration || 2500);
    return () => clearTimeout(t);
  }, [tourActive, tourStep]);

  // ── Tour VIP: narración por voz en cada waypoint ───────────────────────────
  useEffect(() => {
    if (!tourActive || !vipName || !window.speechSynthesis) return;
    const wp = TOUR_WAYPOINTS[tourStep];
    if (!wp) return;

    let msg = '';
    if (wp.id === 'overview') {
      msg = `Hola, ${vipName}. Bienvenido al cerebro de Neuralia, tres, seis, nueve. Aquí operan ciento cincuenta y cinco agentes de inteligencia artificial en diecinueve equipos, activos las veinticuatro horas del día.`;
    } else if (wp.floor >= 0) {
      msg = `${vipName}, planta ${wp.floor + 1}: ${wp.floorName}. Tienes ${wp.activo} agentes activos, ${wp.enMision} en misión y ${wp.entrenando} en entrenamiento continuo.`;
    } else if (wp.id === 'aynara') {
      msg = `Y aquí estoy yo, Aynara. ${vipName}, soy la inteligencia que coordina todo. Cuando quieras, activa el micrófono y hablamos directamente.`;
    }
    if (!msg) return;

    const utter = new SpeechSynthesisUtterance(msg);
    utter.lang = 'es-ES'; utter.rate = 0.87; utter.pitch = 1.1;
    const trySpeak = () => {
      const voices = window.speechSynthesis.getVoices();
      const esVoice = voices.find(v => v.lang === 'es-ES' && /lucia|mónica|sofia|conchita|paloma/i.test(v.name))
                   || voices.find(v => v.lang === 'es-ES')
                   || voices.find(v => v.lang.startsWith('es'));
      if (esVoice) utter.voice = esVoice;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    };
    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.addEventListener('voiceschanged', trySpeak, { once: true });
    } else { trySpeak(); }

    return () => window.speechSynthesis?.cancel();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourActive, tourStep]);

  // ── Visitante VIP: saludo personalizado en español nativo ─────────────────
  // (solo dispara cuando NO hay tour activo — si hay ?ref el tour VIP lo narra)
  useEffect(() => {
    if (!vipName) return;
    // El tour VIP se encarga de la narración; este saludo es fallback sin tour
    const t = setTimeout(() => {
      if (tourActiveRef.current) return; // tour activo → no duplicar voz
      if (!window.speechSynthesis) return;
      const msg = `Bienvenido, ${vipName}. Neuralia, tres, seis, nueve, te da la bienvenida. Soy Aynara, tu inteligencia operativa. Explora el edificio y contáctame cuando quieras.`;
      const utter = new SpeechSynthesisUtterance(msg);
      utter.lang   = 'es-ES';
      utter.rate   = 0.88;
      utter.pitch  = 1.08;
      const trySpeak = () => {
        const voices = window.speechSynthesis.getVoices();
        const esVoice = voices.find(v => v.lang === 'es-ES' && /lucia|mónica|sofia|conchita|paloma/i.test(v.name))
                     || voices.find(v => v.lang === 'es-ES')
                     || voices.find(v => v.lang.startsWith('es'));
        if (esVoice) utter.voice = esVoice;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utter);
      };
      if (window.speechSynthesis.getVoices().length === 0) {
        window.speechSynthesis.addEventListener('voiceschanged', trySpeak, { once: true });
      } else { trySpeak(); }
    }, 4000);
    return () => { clearTimeout(t); window.speechSynthesis?.cancel(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── getUserMedia intercept → AnalyserNode real ───────────────────────────
  useEffect(() => {
    const origGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
    navigator.mediaDevices.getUserMedia = async (constraints) => {
      const stream = await origGetUserMedia(constraints);
      if (constraints.audio && !analyserRef.current) {
        try {
          const AC = window.AudioContext || window.webkitAudioContext;
          const ac = new AC();
          const src = ac.createMediaStreamSource(stream);
          const analyser = ac.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.75;
          src.connect(analyser);
          analyserRef.current = analyser;
        } catch { /* no-op */ }
      }
      return stream;
    };
    return () => { navigator.mediaDevices.getUserMedia = origGetUserMedia; };
  }, []);

  // ── MutationObserver sobre el chatbot → transcripción real ───────────────
  useEffect(() => {
    if (!sessionActive) return;
    const el = document.querySelector('[data-testid="chatbot-messages"]');
    if (!el) return;
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        for (const n of m.addedNodes) {
          const txt = n.textContent?.trim();
          if (!txt || txt.length < 4) continue;
          const isAssistant = n.querySelector?.('[data-testid*="assistant"]') !== null;
          setTranscriptLines((prev) => [...prev.slice(-9), { who: isAssistant ? 'AYNARA' : 'USUARIO', text: txt.slice(0, 78) }]);
        }
      }
    });
    obs.observe(el, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [sessionActive]);

  // ── Máquina de estados + transcripción simulada ───────────────────────────
  const startSession = useCallback(() => {
    setSessionActive(true);
    setOrbState('listening');
    demoIdxRef.current = 0;

    const states = ['listening', 'thinking', 'responding', 'listening', 'thinking', 'responding'];
    let si = 0;
    stateTimerRef.current = setInterval(() => {
      si = (si + 1) % states.length;
      setOrbState(states[si]);
      setActiveToolIdx(Math.floor(Math.random() * TOOLS.length));
      setTimeout(() => setActiveToolIdx(-1), 2200);
    }, 3800 + Math.random() * 1500);

    transcTimerRef.current = setInterval(() => {
      setTranscriptLines((prev) => {
        const line = DEMO_LINES[demoIdxRef.current % DEMO_LINES.length];
        demoIdxRef.current++;
        return [...prev.slice(-9), line];
      });
    }, 2600);
  }, []);

  const endSession = useCallback(() => {
    setSessionActive(false);
    setOrbState('idle');
    setActiveToolIdx(-1);
    clearInterval(stateTimerRef.current);
    clearInterval(transcTimerRef.current);
  }, []);

  useEffect(() => () => { clearInterval(stateTimerRef.current); clearInterval(transcTimerRef.current); }, []);

  // ── Sonido ambiente ciudad (Web Audio API procedural) ────────────────────
  const startAmbientSound = useCallback(() => {
    try {
      if (!ambientCtxRef.current) {
        const ac = new (window.AudioContext || window.webkitAudioContext)();
        // Brownian noise = rumor de ciudad / tráfico lejano
        const bufSize = 3 * ac.sampleRate;
        const buf = ac.createBuffer(2, bufSize, ac.sampleRate);
        for (let ch = 0; ch < 2; ch++) {
          const d = buf.getChannelData(ch);
          let last = 0;
          for (let i = 0; i < bufSize; i++) {
            const w = Math.random() * 2 - 1;
            d[i] = (last + 0.022 * w) / 1.022;
            last = d[i];
            d[i] *= 3.2;
          }
        }
        const src = ac.createBufferSource();
        src.buffer = buf;
        src.loop = true;
        const lp = ac.createBiquadFilter();
        lp.type = 'lowpass'; lp.frequency.value = 280; lp.Q.value = 0.6;
        const gain = ac.createGain();
        gain.gain.value = 0;
        src.connect(lp); lp.connect(gain); gain.connect(ac.destination);
        src.start();
        ambientCtxRef.current  = ac;
        ambientGainRef.current = gain;
      }
      ambientCtxRef.current.resume();
      ambientGainRef.current.gain.setTargetAtTime(0.09, ambientCtxRef.current.currentTime, 1.5);
    } catch { /* no-op */ }
  }, []);

  const stopAmbientSound = useCallback(() => {
    try {
      if (ambientGainRef.current && ambientCtxRef.current) {
        ambientGainRef.current.gain.setTargetAtTime(0, ambientCtxRef.current.currentTime, 0.7);
      }
    } catch { /* no-op */ }
  }, []);

  // ── Greeting audio (preservado) ───────────────────────────────────────────
  const fetchGreeting = async () => {
    if (greetAudioRef.current) return greetAudioRef.current;
    const res = await fetch(`${API}/chat/tts`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: 'Bienvenido a la experiencia Neuralia, tres, seis, nueve. Soy Aynara, la inteligencia que piensa contigo. Automatizo tareas, conecto herramientas y coordino equipos completos. Explora mis módulos y, cuando quieras, hablamos por voz.', voice: 'nova' }),
    });
    if (!res.ok) throw new Error('tts');
    const audio = new Audio(URL.createObjectURL(await res.blob()));
    audio.onended = () => setGreetPlaying(false);
    greetAudioRef.current = audio;
    return audio;
  };
  const playGreeting = async () => {
    // El botón es control maestro de audio: toggle ambient + voz
    if (greetPlaying) {
      greetAudioRef.current?.pause();
      if (greetAudioRef.current) greetAudioRef.current.currentTime = 0;
      setGreetPlaying(false);
      stopAmbientSound();
      return;
    }
    startAmbientSound();
    try { const a = await fetchGreeting(); await a.play(); setGreetPlaying(true); setGreetPrompt(false); } catch { setGreetPrompt(true); }
  };
  useEffect(() => {
    let cancelled = false, playOnce = null;
    (async () => {
      try {
        const audio = await fetchGreeting();
        if (cancelled) return;
        try {
          await audio.play();
          if (!cancelled) { setGreetPlaying(true); startAmbientSound(); }
        }
        catch {
          if (cancelled) return;
          setGreetPrompt(true);
          playOnce = () => {
            startAmbientSound();
            audio.play().then(() => { setGreetPlaying(true); setGreetPrompt(false); }).catch(() => setGreetPrompt(false));
            window.removeEventListener('pointerdown', playOnce);
          };
          window.addEventListener('pointerdown', playOnce);
        }
      } catch { /* sin voz, la página sigue normal */ }
    })();
    return () => { cancelled = true; greetAudioRef.current?.pause(); stopAmbientSound(); if (playOnce) window.removeEventListener('pointerdown', playOnce); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Three.js scene (dynamic import = lazy bundle) ─────────────────────────
  useEffect(() => {
    if (reducedMotion) { setWebglOk(false); return; }
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;
    let cleanup = () => {};

    (async () => {
      // --- dynamic import → solo se descarga en esta ruta ---
      const THREE = await import('three');
      const { EffectComposer } = await import('three/examples/jsm/postprocessing/EffectComposer.js');
      const { RenderPass }     = await import('three/examples/jsm/postprocessing/RenderPass.js');
      const { UnrealBloomPass }= await import('three/examples/jsm/postprocessing/UnrealBloomPass.js');
      if (cancelled) return;

      // WebGL support check
      let renderer;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'high-performance' });
      } catch { setWebglOk(false); return; }

      const W = () => window.innerWidth;
      const H = () => window.innerHeight;
      const isMobile = window.innerWidth < 768;
      const isLowEnd  = (navigator.hardwareConcurrency || 4) <= 2 || isMobile;

      renderer.setPixelRatio(Math.min(window.devicePixelRatio, isLowEnd ? 1 : 2));
      renderer.setSize(W(), H());
      renderer.setClearColor(0x060c28, 1);
      renderer.shadowMap.enabled = !isLowEnd;
      renderer.shadowMap.type = THREE.PCFShadowMap;
      mount.appendChild(renderer.domElement);

      const scene  = new THREE.Scene();
      scene.fog    = new THREE.FogExp2(0x060c28, 0.014);
      const camera = new THREE.PerspectiveCamera(62, W() / H(), 0.1, 120);
      camera.position.set(0, 0, 8.5);

      // ─── Pool de 155 agentes ─────────────────────────────────────────────
      const STATUS_COLORS_3D = {
        activo:      0x22c55e,   // verde  — trabajando (activo)
        'en misión': 0xf2b441,   // dorado corporativo — en misión
        entrenando:  0xef4444,   // rojo   — entrenando/inactivo
      };
      // Pool de 155 agentes → referencia al array de nivel módulo
      const agentPool = AGENT_POOL_DATA;

      // ─── TORRE NEURALIA HQ — Estilo Torre Interbank de Lima ──────────────────
      const FLOORS        = 10;
      const WIN_PER_FRONT = [11,11,11,11,11,11,11,11,11,11]; // 110 ventanas arco frontal
      const WIN_PER_BACK  = [5, 5, 5, 4, 4, 4, 5, 5, 4, 4];  //  45 ventanas arco trasero
      const BLDG_R_TOP   = 2.9;    // radio superior — edificio casi el doble de ancho
      const BLDG_R_BOT   = 3.5;    // radio inferior (base más ancha)
      const BLDG_W       = BLDG_R_TOP * 2;   // alias de compatibilidad
      const BLDG_D       = BLDG_R_BOT * 2;
      const FLOOR_SLB    = 0.07;
      const FLOOR_GAP    = 0.88;
      const BLDG_H       = FLOORS * FLOOR_GAP;  // 8.8
      const BLDG_BOT     = -BLDG_H / 2;         // -4.4

      const building = new THREE.Group();
      building.rotation.z = -0.08;  // inclinación icónica Torre Interbank (~4.6°)
      building.position.set(0.3, 0, 0);
      scene.add(building);

      // ── 1. Cuerpo principal de cristal oscuro (cilindro cónico) ──
      const cylSegments = isLowEnd ? 28 : 48;
      const glassBodyMat = new THREE.MeshStandardMaterial({
        color: 0x040c1e, metalness: 0.22, roughness: 0.04,
        transparent: true, opacity: 0.78,
      });
      const glassBody = new THREE.Mesh(
        new THREE.CylinderGeometry(BLDG_R_TOP, BLDG_R_BOT, BLDG_H, cylSegments, FLOORS, false),
        glassBodyMat
      );
      building.add(glassBody);

      // ── 2. Aletas verticales exteriores (EL sello visual de la Torre Interbank) ──
      const NUM_FINS    = isLowEnd ? 24 : 48;
      const ledStripGeo = new THREE.BoxGeometry(0.062, BLDG_H + 0.30, 0.062);
      const ledMat = new THREE.MeshStandardMaterial({
        color: 0xd8eaff,
        emissive: new THREE.Color(0x2266ff),
        emissiveIntensity: isLowEnd ? 1.4 : 2.2,
        roughness: 0.0, metalness: 0.0,
        transparent: true, opacity: 0.96,
      });
      for (let i = 0; i < NUM_FINS; i++) {
        const angle = (i / NUM_FINS) * Math.PI * 2;
        const rFin  = (BLDG_R_TOP + BLDG_R_BOT) / 2 + 0.04;
        const fin   = new THREE.Mesh(ledStripGeo, ledMat);
        fin.position.set(Math.sin(angle) * rFin, 0, Math.cos(angle) * rFin);
        fin.rotation.y = angle;
        building.add(fin);
      }

      // ── 3. Bandas horizontales por planta (franjas de color luminosas) ──
      const floorBands = [];
      const BAND_COLS = [
        0x0077ff, 0x0044ee, 0x5522ff, 0x9900bb,
        0x0099ff, 0x2266ff, 0x7711ff, 0x0055ff,
        0x0088cc, 0x4400ff, 0x0055ee,
      ];
      const slabGeo = new THREE.BoxGeometry(0.01, 0.01, 0.01); // dummy para cleanup
      const slabMat = new THREE.MeshStandardMaterial({ color: 0x1a3a70 });
      for (let f = 0; f <= FLOORS; f++) {
        const y     = BLDG_BOT + f * FLOOR_GAP;
        const frac  = f / FLOORS;
        const rBand = BLDG_R_BOT + (BLDG_R_TOP - BLDG_R_BOT) * frac + 0.06;
        const torusGeo = new THREE.TorusGeometry(rBand, 0.042, 8, isLowEnd ? 28 : 48);
        const bColor   = BAND_COLS[f % BAND_COLS.length];
        const bandMat  = new THREE.MeshStandardMaterial({
          color: bColor, emissive: new THREE.Color(bColor),
          emissiveIntensity: isLowEnd ? 1.2 : 2.2,
          roughness: 0.0, metalness: 0.0,
        });
        const band = new THREE.Mesh(torusGeo, bandMat);
        band.position.y = y;
        band.rotation.x = Math.PI / 2;
        building.add(band);
        floorBands.push({ mat: bandMat, baseColor: new THREE.Color(bColor) });
      }

      // ── 4. Dummy vars para compatibilidad con cleanup ──
      const colGeo  = slabGeo;  const colMat  = slabMat;
      const glassGeo = slabGeo; const glassMat = slabMat;

      // ── 5. Tapa superior redondeada + pináculo ──
      const crownMat = new THREE.MeshStandardMaterial({
        color: 0x0c2654, metalness: 0.92, roughness: 0.1,
        emissive: 0x0033aa, emissiveIntensity: 0.78,
      });
      const topCap = new THREE.Mesh(
        new THREE.CylinderGeometry(BLDG_R_TOP - 0.04, BLDG_R_TOP + 0.06, 0.28, cylSegments),
        crownMat
      );
      topCap.position.y = BLDG_BOT + BLDG_H + 0.14;
      building.add(topCap);
      const pinnacleGeo = new THREE.CylinderGeometry(0.018, 0.07, 0.9, 6);
      const pinnacleMat = new THREE.MeshStandardMaterial({
        color: 0x44ccff, emissive: new THREE.Color(0x0066dd),
        emissiveIntensity: 1.8, roughness: 0.04, metalness: 0.0,
      });
      const pinnacle = new THREE.Mesh(pinnacleGeo, pinnacleMat);
      pinnacle.position.set(0, BLDG_BOT + BLDG_H + 0.88, 0);
      building.add(pinnacle);

      // ── 6. Paneles de cristal interior curvos por planta (arco frontal) ──
      const glassH    = FLOOR_GAP - FLOOR_SLB - 0.04;
      const WIN_SEGS  = isLowEnd ? 10 : 20;
      const ARC_FRONT = Math.PI * 0.88;
      for (let f = 0; f < FLOORS; f++) {
        const frac = f / Math.max(FLOORS - 1, 1);
        const rF   = BLDG_R_BOT + (BLDG_R_TOP - BLDG_R_BOT) * frac;
        const fy   = BLDG_BOT + f * FLOOR_GAP + (FLOOR_GAP + FLOOR_SLB) / 2;
        for (let s = 0; s < WIN_SEGS; s++) {
          const a1   = -ARC_FRONT / 2 + (s / WIN_SEGS) * ARC_FRONT;
          const aMid = a1 + (ARC_FRONT / WIN_SEGS) / 2;
          const panW = (ARC_FRONT / WIN_SEGS) * rF * 1.02;
          const gp   = new THREE.Mesh(
            new THREE.PlaneGeometry(panW, glassH),
            new THREE.MeshStandardMaterial({
              color: 0x030b1a, transparent: true, opacity: 0.42,
              metalness: 0.82, roughness: 0.04, side: THREE.DoubleSide,
            })
          );
          gp.position.set(Math.sin(aMid) * (rF - 0.02), fy, Math.cos(aMid) * (rF - 0.02));
          gp.rotation.y = aMid;
          building.add(gp);
        }
      }

      // ─── 155 ventanas-agente — GRANDES para ver agentes claramente ────
      const WIN_W   = 0.65;
      const WIN_H   = 0.72;
      const WIN_D   = 0.09;
      const _wCvs = document.createElement('canvas');
      _wCvs.width = 64; _wCvs.height = 84;
      const _wCtx = _wCvs.getContext('2d');
      _wCtx.fillStyle = '#040c18';
      _wCtx.fillRect(0, 0, 64, 84);
      const _wGrd = _wCtx.createRadialGradient(32, 42, 3, 32, 42, 33);
      _wGrd.addColorStop(0,   '#fff9e8');
      _wGrd.addColorStop(0.38,'#ffe4a0');
      _wGrd.addColorStop(0.72,'#a0d8ff');
      _wGrd.addColorStop(1,   '#082040');
      _wCtx.fillStyle = _wGrd;
      _wCtx.fillRect(5, 6, 54, 72);
      _wCtx.fillStyle = '#040c18';
      _wCtx.fillRect(29, 6, 6, 72);
      _wCtx.fillRect(5, 40, 54, 5);
      const _winTex = new THREE.CanvasTexture(_wCvs);
      const winGeo  = new THREE.BoxGeometry(WIN_W, WIN_H, WIN_D);
      const winMat  = new THREE.MeshStandardMaterial({
        map: _winTex, emissiveMap: _winTex,
        color: 0xffffff, emissive: 0xffffff, emissiveIntensity: 0.45,
        roughness: 0.1, metalness: 0.04,
      });
      const agentMesh = new THREE.InstancedMesh(winGeo, winMat, 155);
      agentMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      building.add(agentMesh);

      // Índices "en misión" para pulso
      const enMisionIdx = [];
      const dummy = new THREE.Object3D();
      let aIdx = 0;

      const FRONT_ARC = Math.PI * 0.80;  // ~144° arco frontal (visible desde cámara)
      const BACK_ARC  = Math.PI * 0.70;  // ~126° arco trasero

      for (let f = 0; f < FLOORS; f++) {
        const fy   = BLDG_BOT + f * FLOOR_GAP + FLOOR_GAP * 0.55;
        const frac = f / Math.max(FLOORS - 1, 1);
        const rF   = BLDG_R_BOT + (BLDG_R_TOP - BLDG_R_BOT) * frac;
        const rWin = rF + WIN_D / 2 + 0.025;

        // ── Arco FRONTAL (theta=0 → +Z = hacia la cámara) ──
        const nF = WIN_PER_FRONT[f];
        for (let w = 0; w < nF && aIdx < 155; w++) {
          const theta = -FRONT_ARC / 2 + (w / Math.max(nF - 1, 1)) * FRONT_ARC;
          dummy.position.set(Math.sin(theta) * rWin, fy, Math.cos(theta) * rWin);
          dummy.rotation.set(0, theta, 0);
          dummy.scale.setScalar(1);
          dummy.updateMatrix();
          agentMesh.setMatrixAt(aIdx, dummy.matrix);
          const _ac1 = _getAgentColor(agentPool[aIdx]);
          agentMesh.setColorAt(aIdx, new THREE.Color(_ac1));
          if (agentPool[aIdx]?.status === 'en misión') enMisionIdx.push({ idx: aIdx, hex: _ac1 });
          aIdx++;
        }

        // ── Arco TRASERO (theta=π → -Z = alejado de cámara) ──
        const nB = WIN_PER_BACK[f];
        for (let w = 0; w < nB && aIdx < 155; w++) {
          const theta = Math.PI - BACK_ARC / 2 + (w / Math.max(nB - 1, 1)) * BACK_ARC;
          dummy.position.set(Math.sin(theta) * rWin, fy, Math.cos(theta) * rWin);
          dummy.rotation.set(0, theta, 0);
          dummy.scale.setScalar(1);
          dummy.updateMatrix();
          agentMesh.setMatrixAt(aIdx, dummy.matrix);
          const _ac2 = _getAgentColor(agentPool[aIdx]);
          agentMesh.setColorAt(aIdx, new THREE.Color(_ac2));
          if (agentPool[aIdx]?.status === 'en misión') enMisionIdx.push({ idx: aIdx, hex: _ac2 });
          aIdx++;
        }
      }
      agentMesh.instanceMatrix.needsUpdate = true;
      if (agentMesh.instanceColor) agentMesh.instanceColor.needsUpdate = true;
      agentMesh.count = 155;

      // ─── Aynara en la cima del edificio ──────────────────────────────────
      const aynaraY    = BLDG_BOT + BLDG_H + 1.4;
      const aynaraCore = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.55, 2),
        new THREE.MeshStandardMaterial({
          color: 0x061020, metalness: 0.95, roughness: 0.08,
          emissive: 0x22d3ee, emissiveIntensity: 2.5,
        })
      );
      aynaraCore.position.set(0, aynaraY, 0);
      building.add(aynaraCore);

      const aynaraWire = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.82, 1),
        new THREE.MeshBasicMaterial({ color: CYAN, wireframe: true, transparent: true, opacity: 0.28 })
      );
      aynaraWire.position.set(0, aynaraY, 0);
      building.add(aynaraWire);

      // ── Icosaedro wireframe cian — Neural Network Icon en la cima ──────────
      const icoGeo  = new THREE.IcosahedronGeometry(1.45, 1);
      const icoMat  = new THREE.MeshBasicMaterial({
        color: 0x00ffff, wireframe: true, transparent: true, opacity: 0.58,
      });
      const icoMesh = new THREE.Mesh(icoGeo, icoMat);
      icoMesh.position.set(0, aynaraY, 0);
      building.add(icoMesh);

      const aynaraHalo = new THREE.Mesh(
        new THREE.RingGeometry(2.1, 2.28, 80),
        new THREE.MeshBasicMaterial({ color: 0xf2b441, transparent: true, opacity: 0.0, side: THREE.DoubleSide })
      );
      aynaraHalo.position.set(0, aynaraY, 0);
      aynaraHalo.rotation.x = Math.PI / 2.2;
      // Aros removidos — solo ícono neural limpio (animation loop still references them harmlessly)

      const aynaraHalo2 = new THREE.Mesh(
        new THREE.RingGeometry(1.38, 1.52, 64),
        new THREE.MeshBasicMaterial({ color: 0x22d3ee, transparent: true, opacity: 0.0, side: THREE.DoubleSide })
      );
      aynaraHalo2.position.set(0, aynaraY, 0);
      aynaraHalo2.rotation.x = Math.PI / 3;
      aynaraHalo2.rotation.z = Math.PI / 5;
      // Not added to scene — removed per user request

      // 3 satélites dorados que orbitan a Aynara
      const satGeo  = new THREE.SphereGeometry(0.09, 8, 8);
      const satMats = [
        new THREE.MeshBasicMaterial({ color: 0xf2b441 }),
        new THREE.MeshBasicMaterial({ color: 0x22d3ee }),
        new THREE.MeshBasicMaterial({ color: 0xf2b441 }),
      ];
      const satellites = satMats.map((m) => {
        const sat = new THREE.Mesh(satGeo, m);
        building.add(sat);
        return sat;
      });

      // Haz de luz vertical: Aynara → base del edificio
      const beamGeo = new THREE.CylinderGeometry(0.018, 0.07, BLDG_H + 1.6, 6);
      const beamMat = new THREE.MeshBasicMaterial({ color: CYAN, transparent: true, opacity: 0.09 });
      const beam    = new THREE.Mesh(beamGeo, beamMat);
      beam.position.set(0, BLDG_BOT + (BLDG_H + 1.6) / 2, 0);
      building.add(beam);

      // ── Logo Neuralia 369 — alta resolución, sin neon, iluminado al 100% ────────
      const logoCanvas = document.createElement('canvas');
      logoCanvas.width = 1280; logoCanvas.height = 320;
      const lctx = logoCanvas.getContext('2d');
      lctx.clearRect(0, 0, 1280, 320);
      // Fondo sólido muy oscuro — como el real del logo
      lctx.fillStyle = 'rgba(2,6,22,0.97)';
      lctx.fillRect(0, 0, 1280, 320);
      // Borde elegante doble línea (sutil)
      lctx.strokeStyle = 'rgba(34,130,255,0.70)';
      lctx.lineWidth = 5;
      lctx.strokeRect(4, 4, 1272, 312);
      lctx.strokeStyle = 'rgba(20,80,200,0.38)';
      lctx.lineWidth = 2;
      lctx.strokeRect(11, 11, 1258, 298);
      // "Neuralia" — blanco metalico, sin shadow neon excesivo
      lctx.shadowColor = 'rgba(220,235,255,0.55)';
      lctx.shadowBlur  = 10;
      lctx.fillStyle = '#ffffff';
      lctx.font = 'bold 148px Arial, sans-serif';
      lctx.fillText('Neuralia', 24, 232);
      lctx.shadowBlur = 0;
      // "3" rojo → naranja
      const _g3 = lctx.createLinearGradient(840, 40, 840, 310);
      _g3.addColorStop(0, '#ff2200'); _g3.addColorStop(0.5, '#ff7700'); _g3.addColorStop(1, '#ffaa00');
      lctx.fillStyle = _g3;
      lctx.font = 'bold 168px Arial, sans-serif';
      lctx.fillText('3', 844, 240);
      // "6" amarillo → verde
      const _g6 = lctx.createLinearGradient(990, 40, 990, 310);
      _g6.addColorStop(0, '#ffdd00'); _g6.addColorStop(0.5, '#44dd00'); _g6.addColorStop(1, '#00bb44');
      lctx.fillStyle = _g6;
      lctx.fillText('6', 988, 240);
      // "9" azul → morado
      const _g9 = lctx.createLinearGradient(1130, 40, 1130, 310);
      _g9.addColorStop(0, '#2255ff'); _g9.addColorStop(0.5, '#6622ff'); _g9.addColorStop(1, '#cc00ff');
      lctx.fillStyle = _g9;
      lctx.fillText('9', 1128, 240);
      const logoTex = new THREE.CanvasTexture(logoCanvas);
      const logoPlane = new THREE.Mesh(
        new THREE.PlaneGeometry(6.2, 6.2 * (320 / 1280)),
        new THREE.MeshStandardMaterial({
          map: logoTex, emissiveMap: logoTex,
          emissive: new THREE.Color(0xffffff), emissiveIntensity: 2.8,
          transparent: true, side: THREE.DoubleSide, depthWrite: false,
        })
      );
      // Cima del cilindro (zona alta — como el logo Interbank)
      logoPlane.position.set(0, BLDG_BOT + BLDG_H - 0.9, BLDG_R_TOP + 0.16);
      building.add(logoPlane);

      // ─── Anillo de onda de voz (alrededor de Aynara) ─────────────────────
      const WAVE_N  = isLowEnd ? 64 : 192;
      const wavePos = new Float32Array(WAVE_N * 3);
      const WAVE_R  = 1.5;
      for (let i = 0; i < WAVE_N; i++) {
        const a = (i / WAVE_N) * Math.PI * 2;
        wavePos[i * 3]     = Math.cos(a) * WAVE_R;
        wavePos[i * 3 + 1] = aynaraY;
        wavePos[i * 3 + 2] = Math.sin(a) * WAVE_R;
      }
      const waveGeo = new THREE.BufferGeometry();
      waveGeo.setAttribute('position', new THREE.BufferAttribute(wavePos, 3));
      const waveMat = new THREE.PointsMaterial({
        size: 0.07, color: 0x22d3ee, transparent: true, opacity: 0,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });
      const waveRing = new THREE.Points(waveGeo, waveMat);
      building.add(waveRing);
      let waveData = null;

      // ── Logo Neuralia369 flotante encima del edificio (billboard) ──────────
      const N369_SIGN_Y    = aynaraY + 1.0;
      const n369SignGeo    = new THREE.PlaneGeometry(4.8, 4.8);
      const n369SignMat    = new THREE.MeshStandardMaterial({
        transparent: true, side: THREE.DoubleSide, depthWrite: false, opacity: 0,
        emissive: new THREE.Color(0xffffff), emissiveIntensity: 3.0,
      });
      const n369Sign       = new THREE.Mesh(n369SignGeo, n369SignMat);
      n369Sign.position.set(0, N369_SIGN_Y, 0);
      scene.add(n369Sign);
      // Halo luminoso detrás del logo
      const n369HaloMat = new THREE.MeshBasicMaterial({
        color: 0x001155, transparent: true, opacity: 0.18,
        side: THREE.DoubleSide, depthWrite: false,
      });
      const n369Halo = new THREE.Mesh(new THREE.CircleGeometry(2.6, 32), n369HaloMat);
      n369Halo.position.set(0, N369_SIGN_Y, -0.04);
      scene.add(n369Halo);
      // Cargar imagen real del logo Neuralia369
      const _n369Loader = new THREE.TextureLoader();
      _n369Loader.crossOrigin = 'anonymous';
      _n369Loader.load(
        'https://customer-assets-rejwkqb3.emergentagent.net/job_cf4b6ddd-cd8a-43a0-80c0-50a20361cba4/artifacts/ded66dedcc4213a3_logo%20final%20icono.png',
        (tex) => {
          if (!cancelled) {
            n369SignMat.map        = tex;
            n369SignMat.emissiveMap = tex;
            n369Sign.scale.setScalar(0.4); // empieza pequeño para fade-in
            n369SignMat.needsUpdate = true;
          }
        }
      );

      // ─── Hub de herramientas (órbita exterior) ────────────────────────────
      const toolHub   = new THREE.Group();
      const TOOL_R    = 5.8;
      const toolNodes = TOOLS.map((_, i) => {
        const angle = (i / TOOLS.length) * Math.PI * 2;
        const obj   = new THREE.Object3D();
        obj.position.set(Math.cos(angle) * TOOL_R, Math.sin(i * 0.9) * 1.6, Math.sin(angle) * TOOL_R);
        toolHub.add(obj);
        return obj;
      });
      scene.add(toolHub);

      // ─── Cúpula de cielo nocturno con gradiente profundo ─────────────────
      const skyCvs = document.createElement('canvas');
      skyCvs.width = 2; skyCvs.height = 256;
      const skyCtx = skyCvs.getContext('2d');
      const skyGrd = skyCtx.createLinearGradient(0, 0, 0, 256);
      skyGrd.addColorStop(0.00, '#04061e');
      skyGrd.addColorStop(0.32, '#060c28');
      skyGrd.addColorStop(0.60, '#0a1840');
      skyGrd.addColorStop(0.80, '#12265a');
      skyGrd.addColorStop(0.92, '#1a3878');
      skyGrd.addColorStop(1.00, '#24508a');
      skyCtx.fillStyle = skyGrd;
      skyCtx.fillRect(0, 0, 2, 256);
      const skyDomeTex = new THREE.CanvasTexture(skyCvs);
      const skyDome = new THREE.Mesh(
        new THREE.SphereGeometry(58, 24, 14),
        new THREE.MeshBasicMaterial({ map: skyDomeTex, side: THREE.BackSide, depthWrite: false })
      );
      scene.add(skyDome);
      // Neblina más densa en el horizonte
      scene.fog.density = 0.022;

      // ─── Fondo estelar (contaminación lumínica urbana) ────────────────────
      const starN   = isLowEnd ? 200 : 500;
      const starPos = new Float32Array(starN * 3);
      for (let i = 0; i < starN; i++) {
        starPos[i * 3]     = (Math.random() - 0.5) * 80;
        starPos[i * 3 + 1] = Math.random() * 30 + 5;
        starPos[i * 3 + 2] = (Math.random() - 0.5) * 80;
      }
      const starGeo = new THREE.BufferGeometry();
      starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
      scene.add(new THREE.Points(starGeo, new THREE.PointsMaterial({ size: 0.02, color: 0xffeecc, transparent: true, opacity: 0.14 })));

      // ─── Suelo urbano principal (asfalto oscuro) ──────────────────────────
      const groundGeo = new THREE.PlaneGeometry(120, 120);
      const groundMat = new THREE.MeshStandardMaterial({
        color: 0x080808, roughness: 0.94, metalness: 0.06,
      });
      const ground = new THREE.Mesh(groundGeo, groundMat);
      ground.rotation.x = -Math.PI / 2;
      ground.position.y = BLDG_BOT - 0.05;
      scene.add(ground);

      // ─── VÍA EXPRESA / Autopista Lima — estilo Interbank ─────────────────────
      const ROAD_Y = BLDG_BOT - 0.05;

      // Curva de la autopista: viene desde derecha-frente, gira hacia izquierda-fondo
      const hwCurve = new THREE.CatmullRomCurve3([
        new THREE.Vector3( 28, ROAD_Y,  28),
        new THREE.Vector3( 18, ROAD_Y,  18),
        new THREE.Vector3(  6, ROAD_Y,   9),
        new THREE.Vector3( -3, ROAD_Y,   2),
        new THREE.Vector3(-12, ROAD_Y,  -6),
        new THREE.Vector3(-22, ROAD_Y, -16),
        new THREE.Vector3(-32, ROAD_Y, -28),
      ]);
      const ROAD_W = 17;
      const hwPts  = hwCurve.getPoints(44);

      // ── Superficie de la autopista por segmentos ──
      const roadMat = new THREE.MeshStandardMaterial({
        color: 0x09090e, roughness: 0.88, metalness: 0.08,
      });
      for (let i = 0; i < hwPts.length - 1; i++) {
        const p0  = hwPts[i];
        const p1  = hwPts[i + 1];
        const dir = new THREE.Vector3().subVectors(p1, p0).normalize();
        const segL = p0.distanceTo(p1);
        const mid  = new THREE.Vector3().addVectors(p0, p1).multiplyScalar(0.5);
        const seg  = new THREE.Mesh(new THREE.PlaneGeometry(ROAD_W, segL + 0.1), roadMat);
        seg.rotation.x = -Math.PI / 2;
        seg.rotation.z = Math.atan2(dir.x, dir.z);
        seg.position.copy(mid);
        scene.add(seg);
      }

      // ─── TRAZOS DE LUZ — Long Exposure Effect (Vía Expresa de Lima) ──────────
      const trailRedMat = new THREE.MeshBasicMaterial({
        color: 0xff2200, transparent: true, opacity: 0.82,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });
      const trailOrangeMat = new THREE.MeshBasicMaterial({
        color: 0xff6600, transparent: true, opacity: 0.68,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });
      const trailWhiteMat = new THREE.MeshBasicMaterial({
        color: 0xfff0c0, transparent: true, opacity: 0.84,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });
      const trailYellowMat = new THREE.MeshBasicMaterial({
        color: 0xffee44, transparent: true, opacity: 0.72,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });

      const createTrailLine = (tStart, tEnd, laneOff, mat, w) => {
        const N = 22;
        for (let s = 0; s < N - 1; s++) {
          const t0  = tStart + (tEnd - tStart) * (s / N);
          const t1  = tStart + (tEnd - tStart) * ((s + 1) / N);
          const pt0 = hwCurve.getPoint(Math.min(t0, 0.9999));
          const pt1 = hwCurve.getPoint(Math.min(t1, 0.9999));
          const tan = hwCurve.getTangent(Math.min((t0 + t1) / 2, 0.9999)).normalize();
          const px  = tan.z;
          const pz  = -tan.x;
          const p0  = new THREE.Vector3(pt0.x + px * laneOff, ROAD_Y + 0.07, pt0.z + pz * laneOff);
          const p1  = new THREE.Vector3(pt1.x + px * laneOff, ROAD_Y + 0.07, pt1.z + pz * laneOff);
          const len = p0.distanceTo(p1) + 0.05;
          const mid = new THREE.Vector3().addVectors(p0, p1).multiplyScalar(0.5);
          const dir = new THREE.Vector3().subVectors(p1, p0).normalize();
          const trailMesh = new THREE.Mesh(new THREE.BoxGeometry(w, 0.04, len), mat);
          trailMesh.position.copy(mid);
          trailMesh.rotation.y = Math.atan2(dir.x, dir.z);
          scene.add(trailMesh);
        }
      };

      // Carriles IZQUIERDOS — taillights rojos (vienen hacia cámara)
      createTrailLine(0.04, 0.58, -5.8, trailRedMat, 0.20);
      createTrailLine(0.44, 0.90, -5.8, trailRedMat, 0.15);
      createTrailLine(0.08, 0.62, -3.6, trailOrangeMat, 0.18);
      createTrailLine(0.50, 0.88, -3.6, trailRedMat, 0.13);
      createTrailLine(0.10, 0.65, -1.6, trailRedMat, 0.16);
      createTrailLine(0.52, 0.92, -1.6, trailOrangeMat, 0.12);
      // Carriles DERECHOS — headlights blancos/amarillos (se alejan)
      createTrailLine(0.06, 0.60, 1.6, trailWhiteMat, 0.20);
      createTrailLine(0.46, 0.92, 1.6, trailWhiteMat, 0.15);
      createTrailLine(0.10, 0.64, 3.6, trailYellowMat, 0.18);
      createTrailLine(0.52, 0.90, 3.6, trailWhiteMat, 0.13);
      createTrailLine(0.08, 0.68, 5.8, trailWhiteMat, 0.20);
      createTrailLine(0.48, 0.88, 5.8, trailYellowMat, 0.15);

      // ─── Muros de contención de la autopista (concreto Lima) ─────────────────
      const wallMat = new THREE.MeshStandardMaterial({
        color: 0x242430, roughness: 0.88, metalness: 0.12,
      });
      [-ROAD_W / 2 - 0.15, ROAD_W / 2 + 0.15].forEach(wallOff => {
        for (let i = 0; i < hwPts.length - 1; i++) {
          const p0  = hwPts[i];
          const p1  = hwPts[i + 1];
          const dir = new THREE.Vector3().subVectors(p1, p0).normalize();
          const segL = p0.distanceTo(p1);
          const mid  = new THREE.Vector3().addVectors(p0, p1).multiplyScalar(0.5);
          const px  = dir.z;  const pz = -dir.x;
          const wall = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.52, segL + 0.05), wallMat);
          wall.position.set(mid.x + px * wallOff, ROAD_Y + 0.24, mid.z + pz * wallOff);
          wall.rotation.y = Math.atan2(dir.x, dir.z);
          scene.add(wall);
        }
      });

      // ─── Puente / viaducto en primer plano ────────────────────────────────────
      const bridgeMat = new THREE.MeshStandardMaterial({
        color: 0x1a1a28, roughness: 0.85, metalness: 0.28,
        emissive: 0x040412, emissiveIntensity: 0.22,
      });
      const bridge = new THREE.Mesh(new THREE.BoxGeometry(26, 0.58, 5.0), bridgeMat);
      bridge.position.set(4, ROAD_Y + 1.18, 22);
      bridge.rotation.y = 0.28;
      scene.add(bridge);
      [-2.1, 2.1].forEach(off => {
        const railing = new THREE.Mesh(
          new THREE.BoxGeometry(26, 0.18, 0.12),
          new THREE.MeshStandardMaterial({ color: 0x2a3040, metalness: 0.7, roughness: 0.3 })
        );
        railing.position.set(4, ROAD_Y + 1.6, 22 + off);
        railing.rotation.y = 0.28;
        scene.add(railing);
      });

      // ─── Aceras a ambos lados ────────────────────────────────────────────────
      const sidewalkMat = new THREE.MeshStandardMaterial({
        color: 0x121220, roughness: 0.9, metalness: 0.04,
      });
      [-9.5, 9.5].forEach(xOff => {
        const sw = new THREE.Mesh(new THREE.BoxGeometry(4.0, 0.1, 62), sidewalkMat);
        sw.position.set(xOff, ROAD_Y + 0.04, 6);
        scene.add(sw);
      });

      // ─── Árboles urbanos a lo largo de la vía ─────────────────────────────────
      const treeTrunkGeo = new THREE.CylinderGeometry(0.055, 0.072, 0.92, 7);
      const treeTrunkMat = new THREE.MeshStandardMaterial({ color: 0x1c0d03, roughness: 0.96 });
      const treeFoliageGeo = new THREE.SphereGeometry(0.58, 7, 5);
      const treeFoliageMat = new THREE.MeshStandardMaterial({ color: 0x0b2a10, roughness: 0.88 });
      [
        [-11, -4], [-11, 4], [-11, 12], [-11, 20], [-11, -12],
        [11, -4],  [11, 4],  [11, 12],  [11, 20],  [11, -12],
        [-9, 26],  [9, 26],
      ].forEach(([tx, tz]) => {
        const trunk = new THREE.Mesh(treeTrunkGeo, treeTrunkMat);
        trunk.position.set(tx, BLDG_BOT + 0.44, tz);
        scene.add(trunk);
        const foliage = new THREE.Mesh(treeFoliageGeo, treeFoliageMat);
        foliage.position.set(tx, BLDG_BOT + 1.32, tz);
        scene.add(foliage);
      });

      // ─── Farolas de alumbrado público ──────────────────────────────────────────
      const lampPoleGeo = new THREE.CylinderGeometry(0.02, 0.026, 2.8, 8);
      const lampPoleMat = new THREE.MeshStandardMaterial({
        color: 0x181e2a, metalness: 0.88, roughness: 0.28,
      });
      const lampGlobeMat = new THREE.MeshStandardMaterial({
        color: 0xfff8e0,
        emissive: new THREE.Color(0xfff8e0), emissiveIntensity: 2.8,
      });
      [
        [-7.0, -4], [-7.0,  6], [-7.0, 16], [-7.0, 26],
        [ 7.0, -4], [ 7.0,  6], [ 7.0, 16], [ 7.0, 26],
      ].forEach(([lx, lz]) => {
        const pole = new THREE.Mesh(lampPoleGeo, lampPoleMat);
        pole.position.set(lx, BLDG_BOT + 1.35, lz);
        scene.add(pole);
        const armDir = lx < 0 ? 1 : -1;
        const arm = new THREE.Mesh(new THREE.BoxGeometry(0.65, 0.024, 0.024), lampPoleMat);
        arm.position.set(lx + armDir * 0.32, BLDG_BOT + 2.8, lz);
        scene.add(arm);
        const globe = new THREE.Mesh(new THREE.SphereGeometry(0.1, 8, 6), lampGlobeMat);
        globe.position.set(lx + armDir * 0.68, BLDG_BOT + 2.75, lz);
        scene.add(globe);
        const lampPL = new THREE.PointLight(0xfff8d0, isLowEnd ? 22 : 38, 9.5);
        lampPL.position.set(lx + armDir * 0.68, BLDG_BOT + 2.7, lz);
        scene.add(lampPL);
      });

      // ─── Base circular del edificio (plazoleta) ────────────────────────────────
      const lobbyMat = new THREE.MeshStandardMaterial({
        color: 0x0d1e4a, metalness: 0.72, roughness: 0.28,
        emissive: 0x04102a, emissiveIntensity: 0.5,
      });
      const lobbyBase = new THREE.Mesh(
        new THREE.CylinderGeometry(BLDG_R_BOT + 1.2, BLDG_R_BOT + 1.8, 0.18, 28),
        lobbyMat
      );
      lobbyBase.position.set(building.position.x, BLDG_BOT - 0.09, 0);
      scene.add(lobbyBase);
      const entranceLight = new THREE.PointLight(0xf2b441, 30, 10);
      entranceLight.position.set(building.position.x, BLDG_BOT + 1.8, BLDG_R_BOT + 2.5);
      scene.add(entranceLight);
      // dummy para cleanup
      const archGeo = new THREE.BufferGeometry();
      const archMat = new THREE.MeshStandardMaterial();

      // ─── Edificios de ciudad Lima (horizonte denso y lateral) ────────────────
      const cityBldgData = [
        // Horizonte trasero (Lima residencial y comercial)
        [-18, -26, 4.2, 1.4], [-15, -23, 5.8, 1.6], [-11, -28, 3.8, 1.5],
        [-7,  -25, 4.4, 1.2], [-3,  -27, 6.0, 1.7], [0,   -30, 3.2, 1.3],
        [4,   -25, 5.2, 1.6], [8,   -28, 4.0, 1.4], [12,  -24, 5.5, 1.8],
        [16,  -26, 3.8, 1.3], [20,  -28, 4.8, 2.0], [-24, -30, 6.2, 2.2],
        [24,  -30, 5.4, 1.9], [-28, -28, 4.0, 1.5], [28,  -28, 3.5, 1.4],
        // Segunda fila (más cerca — Lima media distancia)
        [-16, -18, 3.0, 1.1], [-12, -16, 3.8, 1.2], [-8,  -18, 2.8, 1.0],
        [8,   -17, 3.2, 1.1], [12,  -16, 3.6, 1.3], [16,  -18, 2.6, 1.0],
        [-4,  -18, 4.2, 1.4], [3,   -17, 3.0, 1.2],
        // Edificios LATERALES izquierda y derecha
        [-12, -2,  2.8, 1.6], [-12,  8,  3.4, 1.9], [-12, 18,  2.4, 1.4],
        [-14, -10, 3.2, 1.5], [-13, 26,  2.0, 1.2],
        [ 12, -2,  2.6, 1.6], [ 12,  8,  3.2, 1.8], [ 12, 18,  2.6, 1.5],
        [ 14, -10, 3.0, 1.4], [ 13, 26,  2.2, 1.2],
      ];
      const cityMats = [
        new THREE.MeshStandardMaterial({ color: 0x060e18, roughness: 0.88, metalness: 0.32, emissive: 0x020810, emissiveIntensity: 0.38 }),
        new THREE.MeshStandardMaterial({ color: 0x081422, roughness: 0.85, metalness: 0.35, emissive: 0x030c1e, emissiveIntensity: 0.40 }),
        new THREE.MeshStandardMaterial({ color: 0x0a1220, roughness: 0.9,  metalness: 0.30, emissive: 0x040a18, emissiveIntensity: 0.35 }),
      ];
      cityBldgData.forEach(([cx, cz, ch, cw], i) => {
        const bg = new THREE.Mesh(
          new THREE.BoxGeometry(cw, ch, cw * 0.65), cityMats[i % 3]
        );
        bg.position.set(cx, BLDG_BOT + ch / 2, cz);
        scene.add(bg);
        const winCount = Math.floor(4 + Math.random() * 8);
        for (let w = 0; w < winCount; w++) {
          const wp = new THREE.Mesh(
            new THREE.PlaneGeometry(0.1, 0.13),
            new THREE.MeshBasicMaterial({ color: Math.random() > 0.55 ? 0xfff0aa : 0x3366ff, side: THREE.DoubleSide })
          );
          wp.position.set(
            cx + (Math.random() - 0.5) * cw * 0.75,
            BLDG_BOT + Math.random() * (ch - 0.3) + 0.15,
            cz + cw * 0.34
          );
          scene.add(wp);
        }
      });

      // ─── Iluminación corporativa (azul navy + dorado) ─────────────────────
      const ambLight = new THREE.AmbientLight(0x0a1a3f, 1.5);
      scene.add(ambLight);

      const keyLight = new THREE.PointLight(0xf2b441, 45, 65);
      keyLight.position.set(-3, aynaraY + 2, 8);
      if (!isLowEnd) { keyLight.castShadow = true; keyLight.shadow.mapSize.setScalar(1024); }
      scene.add(keyLight);

      const fillLight = new THREE.PointLight(0x22d3ee, 30, 55);
      fillLight.position.set(0, aynaraY + 1, 5);
      scene.add(fillLight);

      const rimLight = new THREE.PointLight(0x3b82f6, 22, 45);
      rimLight.position.set(7, 0, -5);
      scene.add(rimLight);

      const groundLight = new THREE.PointLight(0x1a3a8f, 18, 40);
      groundLight.position.set(0, BLDG_BOT - 1, 0);
      scene.add(groundLight);

      // Luces de acento del cilindro (azul, laterales y frontal) — reducidas para realismo
      const ledGlowL = new THREE.PointLight(0x0055ee, 32, 14);
      ledGlowL.position.set(-(BLDG_R_BOT + 1.2), 0, 0);
      scene.add(ledGlowL);
      const ledGlowR = new THREE.PointLight(0x0055ee, 32, 14);
      ledGlowR.position.set(BLDG_R_BOT + 1.2, 0, 0);
      scene.add(ledGlowR);
      // Luz frontal para la fachada — reducida para no sobreexponer
      const ledGlowF = new THREE.PointLight(0x2244ff, 25, 12);
      ledGlowF.position.set(0, 1.5, BLDG_R_BOT + 2.0);
      scene.add(ledGlowF);

      // Posición inicial de cámara para ver el edificio completo con logo
      camera.position.set(7.5, 3.5, 17);
      camera.lookAt(0, 2.5, 0);

      // ─── Post-procesado: Bloom suave (solo RenderPass + BloomPass) ───────
      const composer = new EffectComposer(renderer);
      composer.addPass(new RenderPass(scene, camera));
      const bloomPass = new UnrealBloomPass(
        new THREE.Vector2(W(), H()),
        isLowEnd ? 0.28 : 0.48,  // strength reducida — LEDs brillan sin sobreexponer toda la fachada
        0.32,                     // radius
        isLowEnd ? 0.82 : 0.75,  // threshold más alto — solo los LEDs más brillantes hacen bloom
      );
      composer.addPass(bloomPass);

      // ─── Raycaster: clic sobre ventana-agente ─────────────────────────────
      const raycaster    = new THREE.Raycaster();
      const mouseNDC     = new THREE.Vector2();

      // ─── Particle burst al hacer clic en agente ───────────────────────────
      const PART_N   = isLowEnd ? 30 : 70;
      const pBufPos  = new Float32Array(PART_N * 3);
      const pBufVel  = new Float32Array(PART_N * 3);
      const pBufGeo  = new THREE.BufferGeometry();
      pBufGeo.setAttribute('position', new THREE.BufferAttribute(pBufPos, 3));
      const pBufMat  = new THREE.PointsMaterial({
        size: 0.09, color: 0xf2b441, transparent: true, opacity: 0,
        blending: THREE.AdditiveBlending, depthWrite: false,
      });
      const pBurst   = new THREE.Points(pBufGeo, pBufMat);
      scene.add(pBurst);
      let pActive = false, pTimer = 0;

      const triggerBurst = (worldPos, color) => {
        pBufMat.color.setHex(color);
        for (let i = 0; i < PART_N; i++) {
          pBufPos[i*3]   = worldPos.x; pBufPos[i*3+1] = worldPos.y; pBufPos[i*3+2] = worldPos.z;
          pBufVel[i*3]   = (Math.random()-0.5) * 0.12;
          pBufVel[i*3+1] = Math.random() * 0.10 + 0.02;
          pBufVel[i*3+2] = (Math.random()-0.5) * 0.12;
        }
        pBufGeo.attributes.position.needsUpdate = true;
        pBufMat.opacity = 0.95; pActive = true; pTimer = 0;
      };

      // ─── Glow de aristas del cilindro ────────────────────────────────────────
      const edgesGeo = new THREE.EdgesGeometry(
        new THREE.CylinderGeometry(BLDG_R_TOP, BLDG_R_BOT, BLDG_H, 16, 3, false)
      );
      const edgesMat = new THREE.LineBasicMaterial({ color: 0x4488ff, transparent: true, opacity: 0.08 });
      const edgeLines = new THREE.LineSegments(edgesGeo, edgesMat);
      edgeLines.position.set(building.position.x, 0, 0);
      edgeLines.rotation.z = building.rotation.z;
      scene.add(edgeLines);

      // ─── Suelo mojado reflectante (noche lluviosa, espejo de ciudad) ────────
      const reflMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(42, 58),
        new THREE.MeshStandardMaterial({
          color: 0x020810, metalness: 0.97, roughness: 0.025,
          transparent: true, opacity: 0.84,
          emissive: 0x010406, emissiveIntensity: 0.15,
        })
      );
      reflMesh.rotation.x = -Math.PI / 2;
      reflMesh.position.set(0, BLDG_BOT - 0.014, 8);
      scene.add(reflMesh);

      const onClickAgent = (evt) => {
        if (isDragging) return;   // no clic si se estaba orbitando
        mouseNDC.x = (evt.clientX / W()) * 2 - 1;
        mouseNDC.y = -(evt.clientY / H()) * 2 + 1;
        raycaster.setFromCamera(mouseNDC, camera);
        const hits = raycaster.intersectObject(agentMesh);
        if (hits.length > 0 && hits[0].instanceId !== undefined) {
          const idx = hits[0].instanceId;
          if (idx < agentPool.length) {
            setSelectedAgent(agentPool[idx]);
            // Detectar planta clicada por índice acumulado
            let clickedFloor = 0;
            let cumIdx = 0;
            for (let fi = 0; fi < 10; fi++) {
              cumIdx += TOUR_FLOOR_COUNTS[fi];
              if (idx < cumIdx) { clickedFloor = fi; break; }
            }
            setSelectedFloor(clickedFloor);
            // Particle burst en la posición del agente clicado
            const tmpMat = new THREE.Matrix4();
            agentMesh.getMatrixAt(idx, tmpMat);
            const wPos = new THREE.Vector3();
            wPos.setFromMatrixPosition(tmpMat);
            agentMesh.localToWorld(wPos);
            const statusClr = { activo: 0x22c55e, 'en misión': 0xf2b441, entrenando: 0xef4444 };
            triggerBurst(wPos, statusClr[agentPool[idx]?.status] ?? 0xf2b441);
            // Zoom suave hacia ese agente
            zoomTarget = Math.max(6, camera.position.distanceTo(wPos) * 0.6);
          }
        } else {
          setSelectedAgent(null);
          setSelectedFloor(null);
        }
      };
      renderer.domElement.addEventListener('click', onClickAgent);

      // ─── Hover sobre ventanas-agente (tooltip flotante) ───────────────────
      const onHoverAgent = (evt) => {
        mouseNDC.x = (evt.clientX / W()) * 2 - 1;
        mouseNDC.y = -(evt.clientY / H()) * 2 + 1;
        raycaster.setFromCamera(mouseNDC, camera);
        const hits = raycaster.intersectObject(agentMesh);
        if (hits.length > 0 && hits[0].instanceId !== undefined) {
          const idx = hits[0].instanceId;
          if (idx < agentPool.length) {
            setWinTooltip({ agent: agentPool[idx], x: evt.clientX, y: evt.clientY });
            renderer.domElement.style.cursor = 'pointer';
            return;
          }
        }
        setWinTooltip(null);
        renderer.domElement.style.cursor = isDragging ? 'grabbing' : 'grab';
      };
      const onLeaveCanvas = () => {
        setWinTooltip(null);
        renderer.domElement.style.cursor = 'grab';
      };
      renderer.domElement.addEventListener('pointermove', onHoverAgent);
      renderer.domElement.addEventListener('pointerleave', onLeaveCanvas);

      // ─── Eventos de ratón, resize y scroll ────────────────────────────────
      const mouse   = { x: 0, y: 0 };
      const onMouse = (e) => {
        mouse.x = (e.clientX / W() - 0.5) * 2;
        mouse.y = (e.clientY / H() - 0.5) * 2;
        if (isDragging) {
          const dx = (e.clientX - dragX) / W();
          const dy = (e.clientY - dragY) / H();
          orbitTarget -= dx * Math.PI * 1.8;
          elevTarget   = Math.max(-0.4, Math.min(0.7, elevTarget + dy * 1.2));
          dragX = e.clientX; dragY = e.clientY;
        }
      };
      window.addEventListener('mousemove', onMouse);

      // Drag-to-orbit
      let isDragging = false, dragX = 0, dragY = 0;
      let orbitAngle = 0.25, orbitTarget = 0.25;
      let elevActual = 0.10, elevTarget = 0.10;
      let zoomActual = 12.5, zoomTarget = 12.5;

      const onMouseDown = (e) => { isDragging = true; dragX = e.clientX; dragY = e.clientY; };
      const onMouseUp   = ()    => { isDragging = false; };
      renderer.domElement.addEventListener('mousedown', onMouseDown);
      window.addEventListener('mouseup', onMouseUp);

      // Touch drag-to-orbit
      let touchX = 0, touchY = 0;
      const onTouchStart = (e) => { if (e.touches.length === 1) { isDragging = true; touchX = e.touches[0].clientX; touchY = e.touches[0].clientY; } };
      const onTouchMove  = (e) => {
        if (isDragging && e.touches.length === 1) {
          const dx = (e.touches[0].clientX - touchX) / W();
          const dy = (e.touches[0].clientY - touchY) / H();
          orbitTarget -= dx * Math.PI * 2.0;
          elevTarget   = Math.max(-0.4, Math.min(0.7, elevTarget + dy * 1.4));
          touchX = e.touches[0].clientX; touchY = e.touches[0].clientY;
        }
      };
      const onTouchEnd   = () => { isDragging = false; };
      renderer.domElement.addEventListener('touchstart', onTouchStart, { passive: true });
      renderer.domElement.addEventListener('touchmove',  onTouchMove,  { passive: true });
      renderer.domElement.addEventListener('touchend',   onTouchEnd);

      // Scroll zoom (rueda del ratón)
      const onWheel = (e) => { zoomTarget = Math.max(5.5, Math.min(25, zoomTarget + e.deltaY * 0.012)); };
      window.addEventListener('wheel', onWheel, { passive: true });

      // Teclado: flechas para orbitar
      const onKey = (e) => {
        if (e.key === 'ArrowLeft')  orbitTarget -= 0.18;
        if (e.key === 'ArrowRight') orbitTarget += 0.18;
        if (e.key === 'ArrowUp')    elevTarget   = Math.min(0.7,  elevTarget - 0.1);
        if (e.key === 'ArrowDown')  elevTarget   = Math.max(-0.4, elevTarget + 0.1);
        if (e.key === '+' || e.key === '=') zoomTarget = Math.max(5.5,  zoomTarget - 1.5);
        if (e.key === '-')           zoomTarget = Math.min(25, zoomTarget + 1.5);
        if (e.key === 'Escape')    { zoomTarget = 12.5; orbitTarget = 0.25; elevTarget = 0.10; }
      };
      window.addEventListener('keydown', onKey);

      const onResize = () => {
        camera.aspect = W() / H();
        camera.updateProjectionMatrix();
        renderer.setSize(W(), H());
        composer.setSize(W(), H());
        bloomPass.setSize(W(), H());
      };
      window.addEventListener('resize', onResize);

      // ─── Config lerped (transición suave entre estados del orbe) ──────────
      const cur = { speed: 0.18, glow: 0.28, scale: 1.0, ring: 0.0 };
      const clock = new THREE.Clock();
      let frame;
      let scrollDepth = 0;

      // ─── Constantes para Modo Día/Noche ─────────────────────────────────
      const _cTmp      = new THREE.Color();
      const NIGHT_SKY  = new THREE.Color(0x060c28);
      const DAY_SKY    = new THREE.Color(0x6a2010); // atardecer Lima — cielo cálido visible
      const NIGHT_AMB  = new THREE.Color(0x0a1a3f);
      const DAY_AMB    = new THREE.Color(0xffaa50); // luz ambiente dorada al atardecer
      const NIGHT_KEY  = new THREE.Color(0xf2b441);
      const DAY_KEY    = new THREE.Color(0xff6420); // luz solar naranja intensa
      const NIGHT_FILL = new THREE.Color(0x22d3ee);
      const DAY_FILL   = new THREE.Color(0xff6030); // relleno cálido
      const NIGHT_RIM  = new THREE.Color(0x3b82f6);
      const DAY_RIM    = new THREE.Color(0xffb850); // borde dorado
      const onScroll = () => {
        const m = document.body.scrollHeight - window.innerHeight;
        scrollDepth = m > 0 ? window.scrollY / m : 0;
      };
      window.addEventListener('scroll', onScroll, { passive: true });

      // ─── Bucle de animación ───────────────────────────────────────────────
      const animate = () => {
        const t   = clock.getElapsedTime();
        const cfg = ORB_CFG[orbStateRef.current] || ORB_CFG.idle;
        const LERP = 0.04;

        cur.speed = lerp(cur.speed, cfg.speed, LERP);
        cur.glow  = lerp(cur.glow,  cfg.glow,  LERP);
        cur.scale = lerp(cur.scale, cfg.scale, LERP * 0.5);
        cur.ring  = lerp(cur.ring,  cfg.ring,  LERP);

        // ── Aynara en la cima ──
        aynaraCore.rotation.y = t * 0.38 * cur.speed;
        aynaraCore.rotation.x = Math.sin(t * 0.3) * 0.22;
        aynaraCore.material.emissiveIntensity = 0.6 + cur.glow * 0.65;
        aynaraWire.rotation.y = -t * 0.20 * cur.speed;
        aynaraWire.material.opacity = 0.18 + cur.glow * 0.22;
        // ── Icosaedro cian — rotación continua + pulso de opacidad ──
        icoMesh.rotation.y = t * 0.28;
        icoMesh.rotation.x = t * 0.16;
        icoMat.opacity = 0.38 + Math.sin(t * 1.5) * 0.20;
        aynaraHalo.rotation.z = t * 0.30 * cur.speed;
        aynaraHalo2.rotation.z = -t * 0.48 * cur.speed;
        beamMat.opacity       = 0.07 + Math.sin(t * 1.6) * 0.04;
        keyLight.intensity    = 32 + cur.glow * 20;

        // Logo billboard — billboard + fade-in suave al cargar
        if (n369Sign) {
          n369Sign.quaternion.copy(camera.quaternion);
          if (n369SignMat.map && n369SignMat.opacity < 0.96) {
            // Fade-in + escala desde 0.4 → 1.0
            n369SignMat.opacity = Math.min(n369SignMat.opacity + 0.014, 0.96);
            const sc = 0.4 + 0.6 * (n369SignMat.opacity / 0.96);
            n369Sign.scale.setScalar(sc);
            n369SignMat.needsUpdate = true;
          } else if (n369SignMat.opacity >= 0.96) {
            // Pulso suave una vez cargado
            n369Sign.scale.setScalar(0.96 + Math.sin(t * 0.6) * 0.04);
          }
        }
        if (n369Halo) {
          n369Halo.quaternion.copy(camera.quaternion);
          n369HaloMat.opacity = n369SignMat.map ? (0.16 + Math.sin(t * 0.8) * 0.08) : 0;
        }

        // Satélites orbitando Aynara
        satellites.forEach((sat, i) => {
          const angle = t * 0.85 * cur.speed + (i / 3) * Math.PI * 2;
          sat.position.set(
            Math.cos(angle) * 0.95,
            aynaraY + Math.sin(t * 1.1 + i * 2.09) * 0.28,
            Math.sin(angle) * 0.95
          );
        });

        // Luz de entrada del lobby — pulsa suavemente
        entranceLight.intensity = 22 + Math.sin(t * 0.7) * 8;

        // Ventanas "en misión" pulsan suavemente (distingue de agentes activos)
        if (enMisionIdx.length > 0 && agentMesh.instanceColor) {
          const pulse = 0.44 + 0.56 * (0.5 + 0.5 * Math.sin(t * 2.2));
          enMisionIdx.forEach(({ idx, hex }) => {
            agentMesh.setColorAt(idx, new THREE.Color(hex).multiplyScalar(pulse));
          });
          agentMesh.instanceColor.needsUpdate = true;
        }

        // ── Anillo de onda de voz ──
        waveMat.opacity = lerp(waveMat.opacity, cur.ring * 0.75, 0.06);
        if (cur.ring > 0.05) {
          if (!waveData && analyserRef.current) waveData = new Uint8Array(analyserRef.current.frequencyBinCount);
          if (waveData && analyserRef.current) analyserRef.current.getByteFrequencyData(waveData);
          const wAttr = waveGeo.attributes.position;
          for (let i = 0; i < WAVE_N; i++) {
            const a   = (i / WAVE_N) * Math.PI * 2;
            const amp = waveData
              ? (waveData[i % waveData.length] / 255) * cur.ring
              : (Math.sin(t * 5 + a * 6) * 0.5 + 0.5) * cur.ring * 0.6;
            const r = WAVE_R + amp * 0.4;
            wAttr.setXYZ(i, Math.cos(a) * r, aynaraY + amp * 0.25 * Math.sin(a * 3 + t * 3), Math.sin(a) * r);
          }
          wAttr.needsUpdate = true;
        }

        // ── Hub de herramientas orbita ──
        toolHub.rotation.y = t * 0.06 * Math.max(cur.speed, 0.25);
        const activeTool   = activeToolRef.current;
        const wW = W(), wH = H();
        const svgEl = lineSvgRef.current;

        // Posición de Aynara en pantalla → origen de líneas SVG
        const ayWorld = new THREE.Vector3();
        aynaraCore.getWorldPosition(ayWorld);
        const aySc = ayWorld.clone().project(camera);
        const ax   = isFinite(aySc.x) ? (aySc.x * 0.5 + 0.5) * wW : wW / 2;
        const ay   = isFinite(aySc.y) ? (-aySc.y * 0.5 + 0.5) * wH : wH / 2;

        toolNodes.forEach((obj, i) => {
          const wp  = new THREE.Vector3();
          obj.getWorldPosition(wp);
          const v   = wp.clone().project(camera);
          const sx  = isFinite(v.x) ? (v.x * 0.5 + 0.5) * wW : -9999;
          const sy  = isFinite(v.y) ? (-v.y * 0.5 + 0.5) * wH : -9999;
          const vis = v.z < 1 && v.z > -1 && isFinite(sx) && isFinite(sy) && sx > -100 && sx < wW + 100;
          const isAct = activeTool === i;

          const el = toolElsRef.current[i];
          if (el) {
            el.style.left      = `${sx}px`;
            el.style.top       = `${sy}px`;
            el.style.opacity   = vis ? (isAct ? '1' : '0.72') : '0';
            el.style.filter    = isAct ? `drop-shadow(0 0 8px ${GOLD}) drop-shadow(0 0 16px ${GOLD})` : '';
            el.style.transform = `translate(-50%, -50%) scale(${isAct ? 1.35 : 1})`;
          }
          if (svgEl) {
            const ln = svgEl.querySelector(`line[data-ti="${i}"]`);
            if (ln && vis) {
              ln.setAttribute('x1', ax); ln.setAttribute('y1', ay);
              ln.setAttribute('x2', sx); ln.setAttribute('y2', sy);
              ln.setAttribute('stroke', isAct ? GOLD : CYAN);
              ln.setAttribute('stroke-opacity', isAct ? '0.85' : '0.10');
              ln.setAttribute('stroke-width',   isAct ? '2'    : '0.8');
            } else if (ln) {
              ln.setAttribute('stroke-opacity', '0');
            }
          }
        });

        // ── Particle burst de clic ──
        if (pActive) {
          pTimer += clock.getDelta ? 0.016 : 0.016;
          pBufMat.opacity = Math.max(0, 0.95 - pTimer * 0.9);
          for (let i = 0; i < PART_N; i++) {
            pBufPos[i*3]   += pBufVel[i*3];
            pBufPos[i*3+1] += pBufVel[i*3+1];
            pBufPos[i*3+2] += pBufVel[i*3+2];
            pBufVel[i*3+1] -= 0.0012;
          }
          pBufGeo.attributes.position.needsUpdate = true;
          pTimer += 0.016;
          if (pBufMat.opacity <= 0) { pActive = false; }
        }

        // ── Edge glow edificio ──
        edgesMat.opacity = 0.06 + Math.sin(t * 0.6) * 0.03 + cur.glow * 0.06;

        // ── Bandas de piso pulsantes (Torres Interbank) ──
        if (floorBands && floorBands.length > 0) {
          floorBands.forEach(({ mat }, fi) => {
            const pulse = 0.65 + 0.35 * Math.sin(t * 1.4 + fi * 0.55);
            mat.emissiveIntensity = pulse * (isLowEnd ? 1.2 : 2.2);
          });
        }

        // ── Cámara: tour guiado / drag-orbit / ratón ──
        if (tourActiveRef.current) {
          const ws = TOUR_WAYPOINTS[tourStepRef.current];
          if (ws) {
            camera.position.x += (ws.cam.x - camera.position.x) * 0.06;
            camera.position.y += (ws.cam.y - camera.position.y) * 0.06;
            camera.position.z += (ws.cam.z - camera.position.z) * 0.06;
            camera.lookAt(ws.lookAt.x, ws.lookAt.y, ws.lookAt.z);
          }
        } else {
          // Suavizar orbit / elevación / zoom
          orbitAngle += (orbitTarget - orbitAngle) * 0.05;
          elevActual  += (elevTarget  - elevActual)  * 0.05;
          zoomActual  += (zoomTarget  - zoomActual)  * 0.06;

          // Si no hay drag activo, añadir flota sutil con ratón
          const driftX = isDragging ? 0 : mouse.x * 0.6;
          const driftY = isDragging ? 0 : -mouse.y * 0.4;
          const scrollPull = scrollDepth * 5;

          const tCX = Math.sin(orbitAngle) * (zoomActual - scrollPull) + driftX;
          const tCZ = Math.cos(orbitAngle) * (zoomActual - scrollPull);
          const tCY = elevActual * zoomActual + 1.2 + driftY;

          camera.position.x += (tCX - camera.position.x) * 0.04;
          camera.position.y += (tCY - camera.position.y) * 0.04;
          camera.position.z += (tCZ - camera.position.z) * 0.04;
          camera.lookAt(0, 0.8, 0);
        }

        // ── Modo Día/Noche (lerp suave en colores de iluminación) ──────────
        {
          const targetDay = isDayModeRef.current ? 1 : 0;
          dayLerpRef.current = lerp(dayLerpRef.current, targetDay, 0.018);
          const dl = dayLerpRef.current;
          _cTmp.lerpColors(NIGHT_SKY, DAY_SKY, dl);
          renderer.setClearColor(_cTmp, 1);
          scene.fog.color.copy(_cTmp);
          ambLight.color.lerpColors(NIGHT_AMB, DAY_AMB, dl);
          ambLight.intensity = lerp(1.5, 4.2, dl);
          keyLight.color.lerpColors(NIGHT_KEY, DAY_KEY, dl);
          fillLight.color.lerpColors(NIGHT_FILL, DAY_FILL, dl);
          fillLight.intensity = lerp(30, 20, dl);
          rimLight.color.lerpColors(NIGHT_RIM, DAY_RIM, dl);
          rimLight.intensity = lerp(22, 14, dl);
          groundLight.intensity = lerp(18, 8, dl);
        }

        composer.render();
        frame = requestAnimationFrame(animate);
      };
      animate();

      cleanup = () => {
        cancelAnimationFrame(frame);
        window.removeEventListener('mousemove',  onMouse);
        window.removeEventListener('mouseup',    onMouseUp);
        window.removeEventListener('resize',     onResize);
        window.removeEventListener('scroll',     onScroll);
        window.removeEventListener('wheel',      onWheel);
        window.removeEventListener('keydown',    onKey);
        renderer.domElement.removeEventListener('click',      onClickAgent);
        renderer.domElement.removeEventListener('mousedown',  onMouseDown);
        renderer.domElement.removeEventListener('touchstart', onTouchStart);
        renderer.domElement.removeEventListener('touchmove',  onTouchMove);
        renderer.domElement.removeEventListener('touchend',   onTouchEnd);
        renderer.domElement.removeEventListener('pointermove', onHoverAgent);
        renderer.domElement.removeEventListener('pointerleave', onLeaveCanvas);
        winGeo.dispose();  winMat.dispose();
        waveGeo.dispose(); waveMat.dispose();
        beamGeo.dispose(); beamMat.dispose();
        starGeo.dispose();
        satGeo.dispose();
        edgesGeo.dispose(); edgesMat.dispose();
        pBufGeo.dispose();  pBufMat.dispose();
        logoTex.dispose();
        ledStripGeo?.dispose(); ledMat?.dispose();
        pinnacleGeo?.dispose(); pinnacleMat?.dispose(); crownMat?.dispose();
        icoGeo.dispose(); icoMat.dispose();
        slabGeo?.dispose(); slabMat?.dispose();
        archGeo?.dispose(); archMat?.dispose();
        composer.dispose();
        renderer.dispose();
        if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      };
    })();

    return () => { cancelled = true; cleanup(); };
  }, [reducedMotion]);

  // ── Handlers (preservados) ────────────────────────────────────────────────
  const openAynara = () => {
    const t = document.querySelector('[data-testid="chatbot-toggle"]');
    if (t) t.click();
  };
  const startVoiceWithAynara = () => {
    openAynara();
    startSession();
    setTimeout(() => { const m = document.querySelector('[data-testid="chatbot-mic"]'); if (m) m.click(); }, 700);
  };
  const submitCall = async (e) => {
    e.preventDefault(); setBusy(true); setCallStatus(null);
    try {
      const r = await fetch(`${API}/bland/call`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(callForm) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Error al iniciar la llamada');
      setCallStatus({ ok: true, message: '📞 ¡Listo! Aynara te está llamando en estos momentos.' });
      startSession();
    } catch (err) { setCallStatus({ ok: false, message: err.message }); }
    finally { setBusy(false); }
  };
  const submitActivation = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await fetch(`${API}/contact`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...activation, mensaje: activation.mensaje || 'Solicitud de activación desde la Experiencia 3D', source: 'experiencia_3d_portal' }) });
      if (!r.ok) throw new Error('error');
      setActivationSent(true);
    } catch { setCallStatus({ ok: false, message: 'No se pudo enviar, inténtalo de nuevo.' }); }
    finally { setBusy(false); }
  };
  const SHARE_URL  = 'https://www.neuralia369.com/experiencia';
  const SHARE_TEXT = 'Descubre AYNARA, la experiencia 3D de Neuralia369: la inteligencia que piensa contigo.';
  const shareExperience = async () => {
    if (navigator.share) { try { await navigator.share({ title: 'AYNARA — Experiencia 3D Neuralia369', text: SHARE_TEXT, url: SHARE_URL }); return; } catch {} }
    setSharePanel((p) => !p);
  };
  const copyShareLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch {}
  };
  const SHARE_LINKS = [
    { name: 'WhatsApp', href: `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${SHARE_URL}`)}` },
    { name: 'X',        href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(SHARE_URL)}` },
    { name: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_URL)}` },
    { name: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}` },
  ];

  return (
    <main className="relative min-h-[160vh] text-white" style={{ background: '#0c0501' }} data-testid="experiencia-3d-page">
      <SEO
        title="AYNARA — Experiencia 3D | Neuralia369"
        description="El cerebro de Aynara en vivo: orbe 3D, grafo de herramientas, onda de voz y transcripción en tiempo real. Habla con la IA que piensa contigo."
        path="/experiencia"
        image="/experiencia-3d-cover.jpg"
      />

      {/* ── CSS keyframes (inyectadas una sola vez) ── */}
      <style>{`
        @keyframes hudFadeIn  { from { opacity:0; transform:translateX(-6px) } to { opacity:1; transform:none } }
        @keyframes blink      { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes pulse      { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.7)} }
        @keyframes orbPulse2d { 0%,100%{box-shadow:0 0 60px 20px rgba(34,211,238,0.25)} 50%{box-shadow:0 0 100px 40px rgba(34,211,238,0.45)} }
        @keyframes orbitRing  { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes toolFloat  { 0%,100%{transform:translate(-50%,-50%) translateY(0)} 50%{transform:translate(-50%,-50%) translateY(-4px)} }
        @keyframes tourProgress { from{width:0%} to{width:100%} }
      `}</style>

      {/* ── Escena 3D o fallback 2D ── */}
      {webglOk && !reducedMotion
        ? <div ref={mountRef} className="fixed inset-0 z-0" data-testid="experiencia-3d-canvas" style={{ cursor: 'grab' }} />
        : <Orb2DFallback orbState={orbState} />
      }

      {/* ── Tooltip flotante al hacer hover sobre ventana-agente ── */}
      {winTooltip && (
        <div
          data-testid="experiencia-3d-agent-tooltip"
          style={{
            position: 'fixed',
            left: Math.min(winTooltip.x + 18, window.innerWidth - 210),
            top: Math.max(winTooltip.y - 14, 8),
            zIndex: 100,
            pointerEvents: 'none',
            background: 'rgba(2,8,24,0.94)',
            backdropFilter: 'blur(14px)',
            border: '1px solid rgba(34,211,238,0.45)',
            borderRadius: 12,
            padding: '10px 16px',
            minWidth: 180,
            boxShadow: '0 4px 32px rgba(34,211,238,0.14), 0 0 1px rgba(34,211,238,0.4)',
            animation: 'hudFadeIn 0.18s ease-out',
          }}
        >
          <p style={{ fontFamily: 'monospace', fontSize: 11, letterSpacing: '0.22em', color: '#22d3ee', textTransform: 'uppercase', marginBottom: 4, fontWeight: 700 }}>
            {winTooltip.agent.name}
          </p>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', marginBottom: 6, lineHeight: 1.4 }}>
            {winTooltip.agent.spec}
          </p>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 9,
            letterSpacing: '0.18em', textTransform: 'uppercase',
            padding: '3px 8px', borderRadius: 6,
            background: winTooltip.agent.status === 'activo'
              ? 'rgba(34,197,94,0.18)'
              : winTooltip.agent.status === 'en misión'
              ? 'rgba(242,180,65,0.18)'
              : 'rgba(239,68,68,0.18)',
            color: winTooltip.agent.status === 'activo'
              ? '#22c55e'
              : winTooltip.agent.status === 'en misión'
              ? '#f2b441'
              : '#ef4444',
            border: `1px solid ${winTooltip.agent.status === 'activo'
              ? 'rgba(34,197,94,0.35)'
              : winTooltip.agent.status === 'en misión'
              ? 'rgba(242,180,65,0.35)'
              : 'rgba(239,68,68,0.35)'}`,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />
            {winTooltip.agent.status}
          </span>
        </div>
      )}

      {/* ── SVG overlay: líneas herramienta → orbe ── */}
      <svg
        ref={lineSvgRef}
        style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 }}
        aria-hidden="true"
      >
        {TOOLS.map((_, i) => (
          <line key={i} data-ti={i} x1={0} y1={0} x2={0} y2={0} stroke={CYAN} strokeOpacity={0} strokeWidth={0.8} />
        ))}
      </svg>

      {/* ── HTML overlay: iconos de herramientas ── */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 3 }} aria-hidden="true">
        {TOOLS.map((tool, i) => (
          <div
            key={tool.id}
            ref={(el) => { toolElsRef.current[i] = el; }}
            data-testid={`aynara-tool-${tool.id}`}
            style={{
              position: 'absolute', left: 0, top: 0, opacity: 0,
              transition: 'filter 0.25s ease, transform 0.25s ease',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            }}
            onMouseEnter={() => setToolHint(tool.name)}
            onMouseLeave={() => setToolHint(null)}
          >
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: 'rgba(10,26,63,0.85)', backdropFilter: 'blur(6px)',
              border: `1px solid rgba(34,211,238,0.28)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
            }}>
              <img
                src={tool.logo} alt={tool.name}
                style={{ width: 22, height: 22, objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <span style={{
              fontFamily: 'monospace', fontSize: 8, letterSpacing: '0.12em', color: 'rgba(34,211,238,0.7)',
              textTransform: 'uppercase', whiteSpace: 'nowrap',
            }}>{tool.name}</span>
          </div>
        ))}
      </div>

      {/* ── Transcripción HUD (absolute dentro del hero) ── */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 15 }}>
        <TranscriptionHUD lines={transcriptLines} isActive={sessionActive} />
      </div>

      {/* ── Indicador de estado del orbe ── */}
      {sessionActive && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 20, pointerEvents: 'none' }}>
          <OrbStateChip orbState={orbState} />
        </div>
      )}

      {/* ── Panel de tour guiado ── */}
      {tourActive && (
        <TourPanel
          step={tourStep}
          vipName={vipName}
          onEnd={() => {
            setTourActive(false);
            setTourStep(0);
            sessionStorage.setItem('neuralia-tour-seen', '1');
          }}
        />
      )}

      {/* ── Botón de audio maestro (ambiente ciudad + voz Aynara) ── */}
      <button
        type="button" onClick={playGreeting}
        className={`fixed right-6 top-24 z-40 flex h-11 w-11 items-center justify-center rounded-full border backdrop-blur-md transition ${greetPlaying ? 'border-[#f2b441] bg-[#f2b441]/20 text-[#f2b441]' : 'border-white/20 bg-[#040a14]/80 text-slate-300 hover:border-[#f2b441] hover:text-[#f2b441]'}`}
        title={greetPlaying ? 'Silenciar audio' : 'Activar sonido ciudad + voz Aynara'}
        data-testid="experiencia-3d-voice-toggle"
      >
        {greetPlaying ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      {/* ── Botón Modo Día/Noche ── */}
      <button
        type="button"
        onClick={() => setIsDayMode(d => !d)}
        className={`fixed right-6 top-36 z-40 flex h-11 w-11 items-center justify-center rounded-full border backdrop-blur-md transition ${isDayMode ? 'border-[#ffd060] bg-[#ffd060]/20 text-[#ffd060]' : 'border-white/20 bg-[#040a14]/80 text-slate-400 hover:border-[#ffd060] hover:text-[#ffd060]'}`}
        title={isDayMode ? 'Cambiar a modo noche' : 'Cambiar a modo atardecer'}
        data-testid="experiencia-3d-day-night-toggle"
      >
        {isDayMode ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* ── Banner Visitante VIP ── */}
      {vipName && (
        <div
          style={{
            position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
            zIndex: 55, pointerEvents: 'none',
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'rgba(3,7,18,0.94)', backdropFilter: 'blur(16px)',
            border: '1px solid rgba(242,180,65,0.45)',
            borderRadius: 99, padding: '6px 16px 6px 10px',
            boxShadow: '0 0 30px rgba(242,180,65,0.22)',
            animation: 'hudFadeIn 0.5s ease-out',
          }}
          data-testid="experiencia-3d-vip-banner"
        >
          <span style={{ width:7, height:7, borderRadius:'50%', background:GOLD, animation:'pulse 1.8s infinite', flexShrink:0 }} />
          <span style={{ fontFamily:'monospace', fontSize:9, letterSpacing:'0.22em', color:GOLD, textTransform:'uppercase' }}>
            Bienvenido, {vipName}
          </span>
          <span style={{ fontFamily:'monospace', fontSize:8, color:'rgba(34,211,238,0.6)', letterSpacing:'0.12em' }}>
            · NEURALIA369
          </span>
        </div>
      )}

      {greetPrompt && (
        <button type="button" onClick={playGreeting} className="fixed bottom-24 left-1/2 z-40 -translate-x-1/2 animate-pulse rounded-full border border-[#22d3ee]/60 bg-[#040a14]/90 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#7de9ff] backdrop-blur-md" data-testid="experiencia-3d-greet-prompt">
          Toca la pantalla para escuchar a Aynara
        </button>
      )}

      {/* ─── Panel lateral izquierdo: título sin bloquear el edificio ──── */}
      <div
        data-testid="experiencia-3d-hero-panel"
        style={{
          position: 'fixed', left: 24, bottom: 120, zIndex: 20,
          pointerEvents: 'none', maxWidth: 340,
        }}
      >
        <p
          style={{ fontFamily:'monospace', fontSize:8, letterSpacing:'0.35em', color:'rgba(34,211,238,0.4)', textTransform:'uppercase', marginBottom:4 }}
        >
          Arrastra · Rueda = zoom · Flechas · Clic agente
        </p>
        <p
          style={{ fontFamily:'monospace', fontSize:9, letterSpacing:'0.5em', color:'rgba(34,211,238,0.8)', textTransform:'uppercase', marginBottom:8 }}
          data-testid="experiencia-3d-kicker"
        >
          El cerebro de Aynara · en vivo
        </p>
        <h1
          style={{
            fontFamily:'monospace', fontWeight:900, fontSize:38, letterSpacing:'0.12em',
            background:'linear-gradient(120deg, #7de9ff 0%, #22d3ee 40%, #3b82f6 80%)',
            WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', backgroundClip:'text',
            filter:'drop-shadow(0 0 28px rgba(34,211,238,0.55))',
            margin:'0 0 8px', lineHeight:1,
          }}
          data-testid="experiencia-3d-title"
        >
          AYNARA
        </h1>
        <p style={{ fontSize:11, color:'rgba(180,220,255,0.65)', lineHeight:1.6, margin:'0 0 12px' }} data-testid="experiencia-3d-tagline">
          155 agentes trabajando en tiempo real
        </p>
        <div style={{ display:'flex', flexWrap:'wrap', gap:5 }} data-testid="experiencia-3d-stats">
          {['19 equipos', '155 agentes IA', '24/7 activos'].map((chip) => (
            <span key={chip} style={{ borderRadius:99, border:'1px solid rgba(34,211,238,0.3)', background:'rgba(34,211,238,0.08)', color:'rgba(180,230,255,0.85)', fontSize:9, fontFamily:'monospace', padding:'3px 9px', letterSpacing:'0.06em' }}>{chip}</span>
          ))}
        </div>
      </div>

      {/* ─── Barra inferior: CTAs flotantes ─────────────────────────────── */}
      <div
        data-testid="experiencia-3d-cta-bar"
        style={{
          position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)',
          zIndex:25, display:'flex', flexWrap:'wrap', gap:8, alignItems:'center',
          justifyContent:'center',
          background:'rgba(10,5,1,0.88)', backdropFilter:'blur(20px)',
          border:'1px solid rgba(255,150,50,0.25)',
          borderRadius:99, padding:'10px 20px',
          boxShadow:'0 8px 40px rgba(0,0,0,0.6), 0 0 40px rgba(255,120,30,0.12)',
        }}
      >
        <button
          type="button" onClick={startVoiceWithAynara}
          style={{ display:'inline-flex', alignItems:'center', gap:6, borderRadius:99, padding:'8px 18px', fontSize:12, fontWeight:700, background:'#ff8822', color:'#0a0200', border:'none', cursor:'pointer' }}
          data-testid="experiencia-3d-cta-voz"
        >
          <Mic size={14} /> Hablar con Aynara
        </button>
        <button
          type="button" onClick={openAynara}
          style={{ display:'inline-flex', alignItems:'center', gap:6, borderRadius:99, padding:'8px 18px', fontSize:12, fontWeight:700, background:'rgba(255,136,34,0.15)', color:'#ffbb66', border:'1px solid rgba(255,136,34,0.45)', cursor:'pointer' }}
          data-testid="experiencia-3d-cta-aynara"
        >
          <Sparkles size={14} /> Chat
        </button>
        <a
          href="https://wa.me/17872981758" target="_blank" rel="noopener noreferrer"
          style={{ display:'inline-flex', alignItems:'center', gap:6, borderRadius:99, padding:'8px 18px', fontSize:12, fontWeight:700, background:'rgba(255,255,255,0.05)', color:'rgba(255,220,150,0.8)', border:'1px solid rgba(255,255,255,0.1)', textDecoration:'none' }}
          data-testid="experiencia-3d-cta-whatsapp"
        >
          <MessageCircle size={14} /> WhatsApp
        </a>
        <button
          type="button"
          onClick={() => { setCallForm({ phone_number: '', visitor_name: '' }); setCallStatus(null); }}
          style={{ display:'inline-flex', alignItems:'center', gap:6, borderRadius:99, padding:'8px 18px', fontSize:12, fontWeight:700, background:'rgba(255,255,255,0.05)', color:'rgba(255,220,150,0.7)', border:'1px solid rgba(255,255,255,0.1)', cursor:'pointer' }}
          data-testid="experiencia-3d-cta-llamar"
        >
          <PhoneCall size={14} /> Llamar
        </button>
        {sessionActive && (
          <button
            type="button" onClick={endSession}
            style={{ display:'inline-flex', alignItems:'center', gap:6, borderRadius:99, padding:'8px 18px', fontSize:12, fontWeight:700, background:'rgba(239,68,68,0.15)', color:'#fca5a5', border:'1px solid rgba(239,68,68,0.4)', cursor:'pointer' }}
            data-testid="experiencia-3d-end-session"
          >
            <Radio size={14} /> Finalizar
          </button>
        )}
        <button
          type="button" onClick={shareExperience}
          style={{ display:'inline-flex', alignItems:'center', gap:6, borderRadius:99, padding:'8px 16px', fontSize:12, fontWeight:700, background:'rgba(255,255,255,0.04)', color:'rgba(255,220,150,0.6)', border:'1px solid rgba(255,255,255,0.08)', cursor:'pointer' }}
          data-testid="experiencia-3d-cta-compartir"
        >
          <Share2 size={14} />
        </button>
        {!tourActive && (
          <button
            type="button"
            onClick={() => { setTourStep(0); setTourActive(true); }}
            style={{ display:'inline-flex', alignItems:'center', gap:6, borderRadius:99, padding:'8px 16px', fontSize:12, fontWeight:700, background:'rgba(255,180,50,0.1)', color:'#ffcc44', border:`1px solid rgba(255,180,50,0.4)`, cursor:'pointer' }}
            data-testid="experiencia-3d-start-tour"
          >
            ▶ Tour 30s
          </button>
        )}
      </div>

      {sharePanel && (
        <div style={{ position:'fixed', bottom:90, left:'50%', transform:'translateX(-50%)', zIndex:30, display:'flex', flexWrap:'wrap', gap:6, background:'rgba(10,5,1,0.95)', backdropFilter:'blur(16px)', border:'1px solid rgba(255,150,50,0.25)', borderRadius:16, padding:'10px 14px' }} data-testid="experiencia-3d-share-panel">
          {SHARE_LINKS.map((lk) => (
            <a key={lk.name} href={lk.href} target="_blank" rel="noopener noreferrer" style={{ borderRadius:99, border:'1px solid rgba(255,255,255,0.12)', padding:'5px 12px', fontSize:11, fontWeight:700, color:'rgba(255,200,120,0.8)', textDecoration:'none' }} data-testid={`experiencia-3d-share-${lk.name.toLowerCase()}`}>{lk.name}</a>
          ))}
          <button type="button" onClick={copyShareLink} style={{ display:'inline-flex', alignItems:'center', gap:5, borderRadius:99, border:'1px solid rgba(255,170,50,0.4)', background:'rgba(255,120,20,0.1)', padding:'5px 12px', fontSize:11, fontWeight:700, color:'#ffcc66', cursor:'pointer' }} data-testid="experiencia-3d-share-copy">
            {copied ? <Check size={12} /> : <Link2 size={12} />} {copied ? 'Copiado' : 'Copiar'}
          </button>
        </div>
      )}

      {/* Sección de scroll (zona 2+) sigue debajo como antes */}
      <div style={{ height:'100vh' }} aria-hidden="true" />

      {/* ─── Zona 2: Módulos del ecosistema ─────────────────────────────── */}
      <section className="relative z-10 mx-auto max-w-5xl px-6 pb-32 pt-10" data-testid="experiencia-3d-zona2">
        <div className="text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-[#3b82f6]">Zona 2 · El ecosistema</p>
          <h2 className="mt-4 text-3xl font-black text-white">9 módulos orbitando un mismo cerebro</h2>
          <p className="mt-3 text-sm text-slate-400">Los mismos nodos que ves girar en el orbe 3D.</p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="experiencia-3d-modules-grid">
          {MODULES.map((m, idx) => (
            <div
              key={m.id}
              onMouseEnter={() => setHoverInfo(m)}
              onMouseLeave={() => setHoverInfo(null)}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#22d3ee]/60 hover:bg-[#22d3ee]/[0.06] hover:shadow-[0_10px_40px_rgba(34,211,238,0.18)]"
              data-testid={`experiencia-3d-module-${m.id}`}
            >
              <div className="absolute -right-8 -top-8 h-20 w-20 rounded-full bg-[#22d3ee]/10 blur-2xl opacity-0 transition-opacity group-hover:opacity-100" />
              <p className="bg-[linear-gradient(90deg,#22d3ee,#3b82f6)] bg-clip-text text-lg font-black text-transparent">{String(idx + 1).padStart(2, '0')}</p>
              <p className="mt-1 text-base font-bold text-white">{m.name}</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Zona 3: Agentes en vivo ─────────────────────────────────────── */}
      <section className="relative z-10 mx-auto max-w-6xl px-6 pb-24 pt-8" data-testid="experiencia-3d-zona3">
        <div className="text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.4em]" style={{ color: CYAN }}>La flota</p>
          <h2 className="mt-4 text-3xl font-black text-white">13 agentes trabajando en vivo</h2>
        </div>
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="experiencia-3d-agents-grid">
          {AGENTS.map((agent) => (
            <div key={agent.name} className="group rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-sm transition hover:border-[#22d3ee]/50" data-testid={`experiencia-3d-agent-${agent.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-white">{agent.name}</p>
                <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase text-slate-400">
                  <span className={`h-2 w-2 animate-pulse rounded-full ${AGENT_STATUS_STYLE[agent.status]}`} />
                  {agent.status}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-400">{agent.spec}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Zona 4: Portal de activación ──────────────────────────────── */}
      <section className="relative z-10 mx-auto max-w-2xl px-6 pb-40 pt-16 text-center" data-testid="experiencia-3d-zona4">
        <div className="rounded-[32px] border border-[#22d3ee]/30 bg-[#040a14]/80 p-10 backdrop-blur-md" style={{ boxShadow: '0 0 80px rgba(34,211,238,0.18)' }}>
          <p className="text-[11px] font-semibold uppercase tracking-[0.4em]" style={{ color: CYAN }}>El portal</p>
          <h2 className="mt-4 text-3xl font-black text-white">Activa tu sistema Neuralia369</h2>
          <p className="mt-3 text-sm text-slate-400">Déjanos tus datos y Aynara preparará tu propuesta de activación.</p>
          {activationSent ? (
            <p className="mt-8 rounded-2xl border border-emerald-400/40 bg-emerald-400/10 px-6 py-4 text-sm font-semibold text-emerald-300" data-testid="experiencia-3d-activation-success">
              Solicitud recibida. Te contactamos en menos de 24h.
            </p>
          ) : (
            <form onSubmit={submitActivation} className="mt-8 space-y-3 text-left">
              <input className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500" placeholder="Tu nombre" required minLength={2} value={activation.nombre} onChange={(e) => setActivation((p) => ({ ...p, nombre: e.target.value }))} data-testid="experiencia-3d-activation-name" />
              <input type="email" className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500" placeholder="Email de trabajo" required value={activation.email} onChange={(e) => setActivation((p) => ({ ...p, email: e.target.value }))} data-testid="experiencia-3d-activation-email" />
              <input className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500" placeholder="Empresa (opcional)" value={activation.empresa} onChange={(e) => setActivation((p) => ({ ...p, empresa: e.target.value }))} data-testid="experiencia-3d-activation-company" />
              <textarea className="min-h-[80px] w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500" placeholder="¿Qué quieres automatizar?" value={activation.mensaje} onChange={(e) => setActivation((p) => ({ ...p, mensaje: e.target.value }))} data-testid="experiencia-3d-activation-message" />
              <button type="submit" disabled={busy} className="w-full rounded-full px-6 py-3.5 text-sm font-bold transition hover:brightness-110 disabled:opacity-50" style={{ background: CYAN, color: '#020a18' }} data-testid="experiencia-3d-activation-submit">
                {busy ? 'Enviando…' : 'Cruzar el portal → Activar Neuralia369'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ─── Modal de llamada ─────────────────────────────────────────────── */}
      {callForm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4 backdrop-blur-sm" data-testid="experiencia-3d-call-modal">
          <div className="w-full max-w-sm rounded-3xl border border-[#22d3ee]/40 bg-[#040a14] p-7">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em]" style={{ color: CYAN }}>Agente de voz</p>
                <h3 className="mt-1 text-xl font-black text-white">Aynara te llama</h3>
              </div>
              <button type="button" onClick={() => setCallForm(null)} className="rounded-full border border-white/15 p-2 text-slate-400 hover:text-white" data-testid="experiencia-3d-call-close" aria-label="Cerrar">✕</button>
            </div>
            <form onSubmit={submitCall} className="mt-5 space-y-3">
              <input className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500" placeholder="Tu nombre (opcional)" value={callForm.visitor_name} onChange={(e) => setCallForm((p) => ({ ...p, visitor_name: e.target.value }))} data-testid="experiencia-3d-call-name" />
              <input className="w-full rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500" placeholder="+34 600 111 222" required value={callForm.phone_number} onChange={(e) => setCallForm((p) => ({ ...p, phone_number: e.target.value }))} data-testid="experiencia-3d-call-phone" />
              <p className="text-[11px] text-slate-500">Formato internacional con +código de país. Llamada gratuita ~2 min en español.</p>
              <button type="submit" disabled={busy} className="w-full rounded-full px-5 py-3 text-sm font-bold disabled:opacity-50" style={{ background: CYAN, color: '#020a18' }} data-testid="experiencia-3d-call-submit">
                {busy ? 'Conectando…' : 'Recibir llamada de Aynara'}
              </button>
              {callStatus && (
                <p className={`rounded-2xl px-4 py-3 text-xs font-semibold ${callStatus.ok ? 'bg-emerald-400/10 text-emerald-300' : 'bg-rose-400/10 text-rose-300'}`} data-testid="experiencia-3d-call-status">
                  {callStatus.message}
                </p>
              )}
            </form>
          </div>
        </div>
      )}
      {/* ─── Panel de Planta Interactivo (clic en ventana 3D) ───────────────── */}
      {selectedFloor !== null && (
        <FloorPanel
          floorIdx={selectedFloor}
          selectedAgent={selectedAgent}
          onClose={() => { setSelectedFloor(null); setSelectedAgent(null); }}
        />
      )}
    </main>
  );
}
